/**
 * In-app fetch mock for `VITE_MOCK=1` — no MSW, just a small router that
 * mimics the shapes in `docs/api-ui.md` and `apps/app/src/routes/*.ts`
 * closely enough to develop the UI without a backend.
 */
import type { CatalogProduct } from '@nopain-newsletter/core/blocks';
import { handleMockRehost } from './rehost';
import { handleMockForms } from './forms';
import { handleMockLandingPages } from './landingPages';
import { handleMockAi } from './ai';
import { handleMockPasskeys } from './passkeys';
import { mockBuyersGroups, mockBuyersOverview, mockBuyersWhy } from './buyers';
import { mockHtmlCheck, mockTemplateKind, mockTranslateHtml } from './htmlCheck';
import { mockSectionOriginal, mockSectionSave } from './sections';
import {
  buildMockTimeline,
  mockAutomations,
  mockAutomationStats,
  mockAutomationVersions,
  mockBrandKit,
  builtinTriggerConfig,
  mockBuiltinFlows,
  mockCampaigns,
  mockConsentHistory,
  mockConsentTexts,
  mockContactDetail,
  mockContactPrediction,
  mockContactHistory,
  mockContactLabels,
  mockContacts,
  mockCounts,
  mockHealthSummary,
  mockLabelCounts,
  mockLabelSettings,
  mockLists,
  mockReviewItems,
  mockReviewStats,
  mockSegments,
  mockSenderDomains,
  mockSenders,
  mockSourceStats,
  mockSendingRoi,
  mockStatsDetail,
  mockStatsList,
  mockTemplates,
  mockWarmupAdmin,
} from './data';
import { handleMockMedia } from './media';
import { handleMockWoo } from './woo';
import { handleMockK8, mockCampaignTags, mockContactEdit, mockContactFields } from './k8';
import { handleMockCustomObjects } from './customObjects';
import { handleMockCustomRoles, mockOwnDeniedCapabilities, withCustomRoles } from './customRoles';
import type {
  BounceContactOverview,
  BounceDetail,
  BounceReason,
  ContactLabel,
  AudienceEstimate,
  Automation,
  Campaign,
  CampaignAudience,
  ChecklistItem,
  ConsentText,
  DmarcAuthResult,
  DmarcDisposition,
  DmarcRecord,
  DmarcReport,
  ImportJob,
  ImportJobKind,
  Invitation,
  KnownSender,
  KnownSenderMatchType,
  LabelSettings,
  MailingList,
  Role,
  Segment,
  SharedBlock,
  SegmentEventCondition,
  SegmentFilter,
  SegmentGroup,
  SegmentLibraryResponse,
  SegmentPurchaseCondition,
  SegmentPredictionCondition,
  Sender,
  PopupSettings,
  TrackingSettings,
  SendCostSetting,
  SenderDomain,
  SenderPaused,
  HealthSummary,
  Template,
  TenantUser,
  ApiTokenRow,
  WarmupAdmin,
  WooSettingsStatus,
  WooWebhookStatus,
  WooOrderSignupStatus,
  BackInStockSettings,
  UnsubscribeDetail,
  FlowLanguageVariant,
} from '../../types/api';

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

/** Fake per-day dashboard numbers: a quiet flow trickle with a campaign spike each week. */
function mockDashboardDaily(from: string, to: string) {
  const out = [];
  for (let ms = Date.parse(from), i = 0; ms <= Date.parse(to); ms += 24 * 60 * 60 * 1000, i++) {
    const spike = i % 7 === 3;
    const sent = spike ? 240 + (i % 5) * 20 : 8 + (i % 4) * 3;
    out.push({
      day: new Date(ms).toISOString().slice(0, 10),
      sent,
      opened: Math.round(sent * 0.42),
      clicked: Math.round(sent * 0.1),
      orders: spike ? 3 + (i % 3) : i % 3 === 0 ? 1 : 0,
      revenue: spike ? 150 + (i % 4) * 30 : i % 3 === 0 ? 40 : 0,
    });
  }
  return out;
}

/** The brand kit's sender details, as the real HTML check gets them. */
function mockSenderContext() {
  return {
    senderDetails: mockBrandKit.companyAddress,
    legalNoticeUrl: mockBrandKit.legalNoticeUrl,
  };
}

let killSwitchEngaged = false;
/** Tags changed on a contact page this session; others fall back to `mockContactLabels`. */
const contactLabelEdits = new Map<string, ContactLabel[]>();
const labelsFor = (id: string): ContactLabel[] =>
  contactLabelEdits.get(id) ?? mockContactLabels(id);

/** Lets tests start from the default tags again. */
export function resetMockContactLabels(): void {
  contactLabelEdits.clear();
}

let healthAttempted: number | null = null;

/** Lets tests show a sender with too few sends to judge. `null` resets to the default sample. */
export function setMockHealthAttempted(next: number | null): void {
  healthAttempted = next;
}

let healthWarmup: HealthSummary['warmup'] | undefined;

/** Lets tests show another warm-up plan, e.g. one on its last day. `undefined` resets to the default. */
export function setMockHealthWarmup(next: HealthSummary['warmup'] | undefined): void {
  healthWarmup = next;
}

let senderPaused: SenderPaused = mockHealthSummary.senderPaused;

/** Lets tests (and local dev) show a halted or auto-paused sender. `null` resets to running. */
export function setMockSenderPaused(next: SenderPaused | null): void {
  senderPaused = next ?? mockHealthSummary.senderPaused;
}

/** Lets tests start with the kill switch engaged. */
/** A rough stand-in for the API's plain-text part (core `buildTextPart`): links as "text (url)", paragraphs kept, no markup. */
function mockPlainText(html: string): string {
  const text = html
    .replace(/<a\s[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi, '$2 ($1)')
    .replace(/<\/(p|div|h[1-6]|li|tr)>|<br\s*\/?>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text === '' ? '' : `${text}\n`;
}

export function setMockKillSwitch(engaged: boolean): void {
  killSwitchEngaged = engaged;
}
interface ExportFilters {
  q?: string;
  status?: string;
  label?: string;
}

let mockTotpEnabled = true;
let mockRole: Role = 'admin';
let mockDisplayName: string | null = null;

export function resetMockDisplayName(): void {
  mockDisplayName = null;
}

/** Lets tests see the UI as an editor or viewer (read-only screens). */
export function setMockRole(role: Role): void {
  mockRole = role;
}

/** Code the mock treats as "the fifth wrong one" → 429 totp_locked. */
export const MOCK_LOCKING_CODE = '999999';

/** The 30-minute step-up window ("sudo mode"): every accepted code opens it, like the API. */
const STEP_UP_WINDOW_MS = 30 * 60 * 1000;
let mockStepUpUntil: number | null = null;

/** Opens the mock window until `until` (epoch ms), or closes it with `null`. Tests use it for a deterministic state. */
export function setMockStepUpWindow(until: number | null): void {
  mockStepUpUntil = until;
}

export function resetMockStepUp(): void {
  mockStepUpUntil = null;
}

/** Bounces whose contact got the corrected address (`POST /suppressions/bounces/:id/fix-address`). */
const fixedBounceIds = new Set<string>();
/** Bounces whose newsletter went once more to the fixed address. */
const resentBounceIds = new Set<string>();

export function resetMockFixedAddresses(): void {
  fixedBounceIds.clear();
  resentBounceIds.clear();
}

function openMockStepUpWindow(): void {
  mockStepUpUntil = Date.now() + STEP_UP_WINDOW_MS;
}

/**
 * The step-up check of the window-aware routes: a code (123456 or any recovery code) passes and
 * renews the window; without a code the window must be open, else 401 step_up_required.
 * The export and the GDPR delete use `mockStepUpError` instead (always a fresh code).
 */
function mockWindowStepUp(
  b: { totpCode?: string; recoveryCode?: string },
  mailFailedCode?: string,
): Response | null {
  const code = b.totpCode || b.recoveryCode;
  if (!code) {
    if (mockStepUpUntil !== null && mockStepUpUntil > Date.now()) return null;
    return json({ error: 'step_up_required' }, 401);
  }
  if (code === MOCK_LOCKING_CODE) return json({ error: 'totp_locked' }, 429);
  if (mailFailedCode !== undefined && b.totpCode === mailFailedCode) {
    return json({ error: 'mail_failed' }, 502);
  }
  if (b.totpCode !== undefined && b.totpCode !== '123456') {
    return json({ error: 'invalid_totp' }, 401);
  }
  openMockStepUpWindow();
  return null;
}

/** The step-up check of the real routes (lib/stepUp.ts): a code is required, 123456 or any recovery code passes. */
function mockStepUpError(body: unknown): Response | null {
  const b = (body ?? {}) as { totpCode?: string; recoveryCode?: string };
  if (!b.totpCode && !b.recoveryCode) return json({ error: 'invalid_request' }, 400);
  if (b.totpCode === MOCK_LOCKING_CODE || b.recoveryCode === MOCK_LOCKING_CODE) {
    return json({ error: 'totp_locked' }, 429);
  }
  if (b.totpCode !== undefined && b.totpCode !== '123456') {
    return json({ error: 'invalid_totp' }, 401);
  }
  openMockStepUpWindow();
  return null;
}

/**
 * The mock's stand-in for the API's prepared version (`campaign_snapshots.id`):
 * a counter per campaign that an edit of a draft bumps. Prepare returns it as
 * `snapshotId` and approve must send it back, like the API.
 */
const snapshotVersion = new Map<string, number>();
function mockSnapshotId(campaignId: string): string {
  return `snap-${campaignId}-${snapshotVersion.get(campaignId) ?? 1}`;
}

/** What prepare queues: the sample languages of one send each have their own count, everything else 24,861. */
const MOCK_RECIPIENTS: Record<string, number> = {
  'c-bf-da': 9412,
  'c-bf-de': 11873,
  'c-bf-en': 3576,
};
function mockRecipientCount(campaignId: string): number {
  return MOCK_RECIPIENTS[campaignId] ?? 24861;
}

/** How long the mock leaves an approved campaign `scheduled` before "the cron" starts it. */
export const MOCK_CRON_DELAY_MS = 4000;

/** Code the reset-access mock treats as "the link mail failed to send" → 502 mail_failed (re-review 2026-09-29). */
export const MOCK_MAIL_FAILED_CODE = '654321';

/** Lets tests reach the M2 TOTP-setup screen (every role must enroll). */
export function setMockTotpEnabled(enabled: boolean): void {
  mockTotpEnabled = enabled;
}

// --- Signup popup (apps/app/src/routes/popupSettings.ts) ---
const DEFAULT_POPUP: PopupSettings = {
  enabled: false,
  headline: 'Join NoPain',
  body: 'Get tips for before and after your tattoo, news and offers straight to your inbox.',
  buttonText: 'Sign up',
  successText: 'Thank you! Check your inbox to confirm your signup.',
  listId: null,
  trigger: 'delay',
  delaySeconds: 10,
  scrollPercent: 40,
  frequencyDays: 14,
  showOnMobile: true,
  imageUrl: null,
  backgroundColor: '#ffffff',
  textColor: '#243a53',
  buttonColor: '#208233',
  cornerRadius: 12,
  headingFont: 'poppins',
  dimBackground: true,
  showNameField: false,
  couponCode: null,
  targetMode: 'all',
  targetUrlPart: null,
  translations: {
    da: {
      headline: 'Bliv en del af NoPain',
      body: 'Få gode råd før og efter din tatovering, nyheder og tilbud direkte i indbakken.',
      buttonText: 'Tilmeld',
      successText: 'Tak! Tjek din indbakke og bekræft din tilmelding.',
    },
    de: {
      headline: 'Werde Teil von NoPain',
      body: 'Tipps für vor und nach deinem Tattoo, Neuigkeiten und Angebote direkt in dein Postfach.',
      buttonText: 'Anmelden',
      successText: 'Danke! Bitte bestätige deine Anmeldung in deinem Postfach.',
    },
  },
  updatedAt: 0,
  embedCode: '<script src="https://news.example.test/popup.js" async></script>',
};
let popupSettings: PopupSettings = DEFAULT_POPUP;

// --- Browse tracking (apps/app/src/routes/trackingSettings.ts) ---
let trackingSettings: TrackingSettings = {
  enabled: false,
  lowStockThreshold: 5,
  updatedAt: 0,
  embedCode: '<script src="https://news.example.test/track.js" async></script>',
};

/** Resets the mock tracking settings (off). */
export function resetMockTracking(): void {
  trackingSettings = { ...trackingSettings, enabled: false, lowStockThreshold: 5, updatedAt: 0 };
}

/** Resets the mock popup settings to the defaults (popup off). */
export function resetMockPopup(): void {
  popupSettings = DEFAULT_POPUP;
}

// --- Sending cost per 1,000 emails (apps/app/src/routes/sendingCost.ts) ---
const DEFAULT_SEND_COST: SendCostSetting = {
  costPerThousand: 0.09,
  currency: 'EUR',
  updatedAt: Date.UTC(2026, 9, 26, 12),
};
let sendCostSetting: SendCostSetting = DEFAULT_SEND_COST;

/** Lets tests start with (or reset) a price per 1,000 emails. `null` resets to the default 0.09. */
/** K6: c-sep shown as a sending A/B test with auto-send off, waiting for an admin's pick. */
let abAwaitingPick = false;
export let mockAbPicked: 'a' | 'b' | null = null;
export function setMockAbAwaitingPick(on: boolean): void {
  abAwaitingPick = on;
  mockAbPicked = null;
}

export function setMockSendCost(costPerThousand: number | null): void {
  sendCostSetting =
    costPerThousand === null ? DEFAULT_SEND_COST : { ...DEFAULT_SEND_COST, costPerThousand };
}

/** Made-up orders and revenue per mock campaign, for `/stats/sending-roi`. */
function mockCampaignRevenue(id: string): {
  orders: number;
  revenue: number;
  onHoldOrders: number;
  onHoldRevenue: number;
} {
  const roi = mockStatsDetail[id]?.roi;
  return roi
    ? {
        orders: roi.orders,
        revenue: roi.revenue,
        onHoldOrders: roi.onHoldOrders,
        onHoldRevenue: roi.onHoldRevenue,
      }
    : { orders: 0, revenue: 0, onHoldOrders: 0, onHoldRevenue: 0 };
}

// --- NP-Flow Woo plugin settings (apps/app/src/routes/wooSettings.ts) ---
let wooStatus: WooSettingsStatus = { configured: false, pluginUrl: null, lastRotatedAt: null };
/** `wooTestConnectionOk`: whether the mock "test connection" reports success. Lets tests exercise both outcomes without a real shop. */
let wooTestConnectionOk = false;

/** Lets tests start with (or reset) a configured Woo plugin key. `null` resets to unconfigured. */
export function setMockWooStatus(next: WooSettingsStatus | null): void {
  wooStatus = next ?? { configured: false, pluginUrl: null, lastRotatedAt: null };
}

const MOCK_WOO_DELIVERY_URL = 'https://hooks.eksempel.dk/woo/webhook';
let wooWebhookStatus: WooWebhookStatus = {
  configured: false,
  lastRotatedAt: null,
  deliveryUrl: MOCK_WOO_DELIVERY_URL,
};

/** Lets tests start with (or reset) a configured order webhook secret. `null` resets to unconfigured. */
export function setMockWooWebhookStatus(next: WooWebhookStatus | null): void {
  wooWebhookStatus = next ?? {
    configured: false,
    lastRotatedAt: null,
    deliveryUrl: MOCK_WOO_DELIVERY_URL,
  };
}

const MOCK_ORDER_SIGNUP_ON: WooOrderSignupStatus = {
  enabled: true,
  enabledAt: Date.UTC(2026, 9, 23, 12),
  updatedAt: Date.UTC(2026, 9, 23, 12),
};
let wooOrderSignupStatus: WooOrderSignupStatus = MOCK_ORDER_SIGNUP_ON;

let mockBackInStock: BackInStockSettings = {
  enabled: false,
  subject: null,
  bodyText: null,
  updatedAt: 0,
};
export function resetMockBackInStock(): void {
  mockBackInStock = { enabled: false, subject: null, bodyText: null, updatedAt: 0 };
}

/** Lets tests start with (or reset) the buyers-join-the-newsletter switch. `null` resets to on. */
export function setMockWooOrderSignupStatus(next: WooOrderSignupStatus | null): void {
  wooOrderSignupStatus = next ?? MOCK_ORDER_SIGNUP_ON;
}

/** Lets tests choose whether `POST /settings/woo/test-connection` reports success or failure. */
export function setMockWooTestConnectionOk(ok: boolean): void {
  wooTestConnectionOk = ok;
}

// --- Brugere/sikkerhed: admin-udløst 2FA/adgangskode-nulstilling +
// sessions-tilbagekaldelse (apps/app/src/routes/{users,session}.ts,
// runbook MANGLER item 4/3) ---

/** `u-mikkel` is the fixed "self" id `/auth/me` always returns — kept out of the resettable list below (self-reset is rejected server-side too). */
const userStore = new Map<string, TenantUser>([
  [
    'u-mikkel',
    {
      id: 'u-mikkel',
      email: 'mikkel@example.com',
      role: 'admin',
      totpEnabled: true,
      disabledAt: null,
      createdAt: Date.now() - 90 * 86_400_000,
    },
  ],
  [
    'u-anna',
    {
      id: 'u-anna',
      email: 'anna@example.com',
      role: 'editor',
      totpEnabled: true,
      disabledAt: null,
      createdAt: Date.now() - 60 * 86_400_000,
    },
  ],
  [
    'u-bjorn',
    {
      id: 'u-bjorn',
      email: 'bjorn@example.com',
      role: 'viewer',
      totpEnabled: false,
      disabledAt: null,
      createdAt: Date.now() - 14 * 86_400_000,
    },
  ],
]);
/** Lets tests reset/replace the mock member list. */
export function setMockUsers(next: TenantUser[]): void {
  userStore.clear();
  for (const u of next) userStore.set(u.id, u);
}
/** id → whether that user has a live mock reset token still outstanding (redeemed once, like the real one-time link). */
const mockResetTokens = new Map<string, string>();

// --- API tokens (apps/app/src/routes/apiTokens.ts) ---
const mockApiTokens = new Map<string, ApiTokenRow>();
let mockApiTokensEnabled = true;
/** Lets tests mimic a deployment without the API_TOKENS_ENABLED flag (every route answers 404). */
export function setMockApiTokensEnabled(on: boolean): void {
  mockApiTokensEnabled = on;
}
let mockApiTokensProductionScope = false;
/** Lets tests mimic production, where a token is narrowed to `PRODUCTION_API_TOKEN_ROUTES`. */
export function setMockApiTokensProductionScope(on: boolean): void {
  mockApiTokensProductionScope = on;
}
export function resetMockApiTokens(): void {
  mockApiTokens.clear();
  mockApiTokensEnabled = true;
  mockApiTokensProductionScope = false;
}

// --- invitations: token → invitation, so the public accept mock can redeem it once ---
export const MOCK_EXPIRED_INVITE_TOKEN = 'mock-invite-expired';
/** Password the accept mock treats as the existing account's password. */
export const MOCK_EXISTING_PASSWORD = 'existing-password-1';
/** New-user password the accept mock reports as found in a data breach. */
export const MOCK_PWNED_PASSWORD = 'pwned-password-123';
/** Existing-account password the accept mock answers with a rate limit. */
export const MOCK_RATE_LIMITED_PASSWORD = 'rate-limited-123';
const mockInvites = new Map<string, { invitation: Invitation }>();
/** Lets tests reset the invitations; seeds one expired invite. */
export function resetMockInvites(): void {
  mockInvites.clear();
  mockInvites.set(MOCK_EXPIRED_INVITE_TOKEN, {
    invitation: {
      id: 'inv-expired',
      email: 'old@example.com',
      role: 'viewer',
      expiresAt: Date.now() - 86_400_000,
      acceptedAt: null,
      createdAt: Date.now() - 8 * 86_400_000,
    },
  });
}
resetMockInvites();
const consentTextStore: ConsentText[] = [...mockConsentTexts];
const campaignStore = new Map<string, Campaign>(mockCampaigns.map((c) => [c.id, c]));
let nextId = 100;

// --- fase 5 mock state ---
const automationStore = new Map<string, Automation>(mockAutomations.map((a) => [a.id, a]));
const automationVersionStore = new Map<string, (typeof mockAutomationVersions)[string]>(
  Object.entries(mockAutomationVersions).map(([id, versions]) => [id, [...versions]]),
);
let labelSettingsStore: LabelSettings = { ...mockLabelSettings };
let nextAutomationId = 200;

/** Made-up numbers per step (a little fewer contacts reach each later step), and per A/B path. */
function buildMockStepStats(
  steps: { id: string; type: string; paths?: unknown }[],
  enrolled: number,
) {
  const out: {
    steps: Record<
      string,
      {
        entered: number;
        sent: number;
        opened: number;
        clicked: number;
        ordered: number;
        revenue: number;
      }
    >;
    paths: Record<string, number>;
  } = { steps: {}, paths: {} };
  if (enrolled <= 0) return out;
  steps.forEach((step, i) => {
    const entered = Math.max(1, enrolled - i * 7);
    const mail = step.type === 'send_template';
    out.steps[step.id] = {
      entered,
      sent: mail ? entered : 0,
      opened: mail ? Math.round(entered * 0.4) : 0,
      clicked: mail ? Math.round(entered * 0.1) : 0,
      ordered: 0,
      revenue: 0,
    };
    if (step.type === 'ab_split' && Array.isArray(step.paths)) {
      for (const p of step.paths as { id: string; percent: number }[]) {
        out.paths[`${step.id}:${p.id}`] = Math.round((entered * p.percent) / 100);
      }
    }
  });
  return out;
}
const templateStore = new Map<string, Template>(mockTemplates.map((t) => [t.id, t]));
let nextTemplateId = 300;

// --- mailchimp-import (P7) mock state ---
interface MockImportJob extends ImportJob {
  pendingRows: string[][]; // one array of CSV data rows (Status column) per uploaded file, not yet "processed"
  /** Per uploaded file: how many rows had a first-name column (mock stand-in for the importer's counts). */
  columnPlans: MockColumnPlan[];
}

interface MockColumnPlan {
  recognised: { firstName: string[]; lastName: string[]; language: string[]; country: string[] };
  profile: string[];
  ignored: string[];
}

const MOCK_HEADER_ALIASES: Record<string, keyof MockColumnPlan['recognised']> = {
  'first name': 'firstName',
  fname: 'firstName',
  'last name': 'lastName',
  lname: 'lastName',
  language: 'language',
  lang: 'language',
  locale: 'language',
  country: 'country',
  cc: 'country',
};
const MOCK_PROFILE_HEADERS = new Set([
  'address',
  'region',
  'timezone',
  'member_rating',
  'leid',
  'euid',
  'last_changed',
]);
const MOCK_KNOWN_NON_ATTRIBUTE_HEADERS = new Set(['email address', 'status']);

/** Loose mirror of the importer's header aliasing (`packages/core/src/import/attributes.ts`). */
function mockColumnPlan(csvText: string): MockColumnPlan {
  const plan: MockColumnPlan = {
    recognised: { firstName: [], lastName: [], language: [], country: [] },
    profile: [],
    ignored: [],
  };
  const headerLine = csvText.split('\n')[0] ?? '';
  for (const raw of headerLine.split(',')) {
    // The real importer reads headers upper-cased, so the report lists them that way.
    const name = raw.trim().toUpperCase();
    if (name === '') continue;
    const key = MOCK_HEADER_ALIASES[name.toLowerCase()];
    if (key) plan.recognised[key].push(name);
    else if (MOCK_PROFILE_HEADERS.has(name.toLowerCase())) plan.profile.push(name);
    else if (!MOCK_KNOWN_NON_ATTRIBUTE_HEADERS.has(name.toLowerCase()) && !name.includes('@'))
      plan.ignored.push(name);
  }
  return plan;
}

function emptyMockReport(kind: ImportJobKind): ImportJob['report'] {
  return kind === 'mailchimp_import'
    ? {
        filesProcessed: 0,
        totalRows: 0,
        byStatus: {
          subscribed: 0,
          pending: 0,
          unsubscribed: 0,
          cleaned: 0,
          nonsubscribed: 0,
          unknown: 0,
        },
        contactsCreated: 0,
        consentCategoryCounts: {},
        orderMatchRequiredCount: 0,
        suppressionsAdded: { unsubscribed: 0, hard_bounce: 0 },
        skippedAlreadySuppressed: 0,
        duplicatesSkipped: 0,
        rowErrors: 0,
        attributes: {
          columns: {
            recognised: { firstName: [], lastName: [], language: [], country: [] },
            profile: [],
            ignored: [],
            ignoredValues: {},
          },
          created: {
            names: 0,
            languages: 0,
            countries: 0,
            cities: 0,
            postalCodes: 0,
            regions: 0,
            timezones: 0,
            mailchimpMeta: 0,
          },
          existingContactsFilled: 0,
          refused: { languages: 0, countries: 0, timezones: 0 },
        },
      }
    : {
        filesProcessed: 0,
        totalRows: 0,
        byStatus: { unsubscribed: 0, cleaned: 0, other: 0 },
        suppressionsAdded: { unsubscribed: 0, hard_bounce: 0 },
        alreadySuppressed: 0,
        contactsDeleted: 0,
        rowErrors: 0,
      };
}

const importJobStore = new Map<string, MockImportJob>();
let nextImportJobId = 1;

// --- dmarc-cutover (P8, docs/decisions.md "DMARC-overgang 28/9") mock state ---
const DMARC_AUTO_CHECKLIST_KEYS = [
  'full_list_reached_one_day_no_autopause',
  'dmarc_14_days_no_unknown_sources',
  'mailchimp_unsubscribe_sync_done',
] as const;
const DMARC_MANUAL_CHECKLIST_KEYS = [
  'woo_order_mails_unaffected',
  'dmarc_policy_pct_100',
  'k2_k3_dkim_removed',
  'elastic_email_dkim_clarified',
  'mailchimp_downgraded_to_free',
] as const;

interface MockDmarcReport extends DmarcReport {
  records: DmarcRecord[];
}

const dmarcReportStore = new Map<string, MockDmarcReport>();
let nextDmarcReportId = 1;
let nextDmarcRecordId = 1;
const dmarcKnownSenderStore = new Map<string, KnownSender>();
let nextDmarcKnownSenderId = 1;

function freshDmarcChecklist(): Map<string, ChecklistItem> {
  const m = new Map<string, ChecklistItem>();
  const now = Date.now();
  for (const key of DMARC_AUTO_CHECKLIST_KEYS) {
    m.set(key, {
      key,
      kind: 'auto',
      checked: false,
      checkedBy: null,
      checkedAt: null,
      note: null,
      updatedAt: now,
    });
  }
  for (const key of DMARC_MANUAL_CHECKLIST_KEYS) {
    m.set(key, {
      key,
      kind: 'manual',
      checked: false,
      checkedBy: null,
      checkedAt: null,
      note: null,
      updatedAt: now,
    });
  }
  return m;
}

const dmarcChecklistStore = freshDmarcChecklist();

function dmarcXmlTag(xml: string, name: string): string | null {
  const m = new RegExp(`<${name}>([^<]*)</${name}>`).exec(xml);
  return m ? (m[1] ?? '') : null;
}

/**
 * Deliberately minimal — the real parser (packages/core/src/dmarc/parser.ts)
 * handles gzip/zip and the full RFC 7489 structure with hardened XML
 * parsing; this mock only needs to extract enough from a plain-text DMARC
 * aggregate-report XML fixture to drive the UI in dev/tests, so it only
 * supports plain `.xml` uploads (see the page's `accept=".xml"`).
 */
function parseMockDmarcXml(
  xml: string,
): { report: Omit<MockDmarcReport, 'id' | 'sourceFileName' | 'uploadedBy' | 'uploadedAt'> } | null {
  const orgName = dmarcXmlTag(xml, 'org_name');
  const reportId = dmarcXmlTag(xml, 'report_id');
  const policyDomain = dmarcXmlTag(xml, 'domain');
  const policyP = dmarcXmlTag(xml, 'p');
  if (!orgName || !reportId || !policyDomain || !policyP) return null;
  const begin = Number(dmarcXmlTag(xml, 'begin') ?? 0);
  const end = Number(dmarcXmlTag(xml, 'end') ?? 0);
  const policySp = dmarcXmlTag(xml, 'sp');
  const pctRaw = dmarcXmlTag(xml, 'pct');
  const email = dmarcXmlTag(xml, 'email');

  const records: DmarcRecord[] = [];
  const recordMatches = xml.match(/<record>[\s\S]*?<\/record>/g) ?? [];
  for (const rec of recordMatches) {
    const sourceIp = dmarcXmlTag(rec, 'source_ip');
    const count = Number(dmarcXmlTag(rec, 'count') ?? 0);
    const disposition = dmarcXmlTag(rec, 'disposition');
    const dkimResult = dmarcXmlTag(rec, 'dkim');
    const spfResult = dmarcXmlTag(rec, 'spf');
    const headerFrom = dmarcXmlTag(rec, 'header_from');
    if (!sourceIp || !disposition || !dkimResult || !spfResult || !headerFrom) continue;
    const authResultsMatch = /<auth_results>[\s\S]*?<\/auth_results>/.exec(rec);
    const authResults = authResultsMatch ? authResultsMatch[0] : '';
    const dkimBlockMatch = /<dkim>[\s\S]*?<\/dkim>/.exec(authResults);
    const dkimBlock = dkimBlockMatch ? dkimBlockMatch[0] : '';
    const spfBlockMatch = /<spf>[\s\S]*?<\/spf>/.exec(authResults);
    const spfBlock = spfBlockMatch ? spfBlockMatch[0] : '';
    records.push({
      id: `dmarc-record-${nextDmarcRecordId++}`,
      sourceIp,
      count,
      disposition: disposition as DmarcDisposition,
      dkimResult: dkimResult as DmarcAuthResult,
      spfResult: spfResult as DmarcAuthResult,
      headerFrom,
      dkimDomain: dmarcXmlTag(dkimBlock, 'domain'),
      dkimSelector: dmarcXmlTag(dkimBlock, 'selector'),
      spfDomain: dmarcXmlTag(spfBlock, 'domain'),
    });
  }

  return {
    report: {
      orgName,
      reportId,
      email,
      dateBegin: begin * 1000,
      dateEnd: end * 1000,
      policyDomain,
      policyP: policyP as DmarcDisposition,
      policySp: (policySp as DmarcDisposition | null) ?? null,
      policyPct: pctRaw ? Number(pctRaw) : null,
      records,
    },
  };
}

function dmarcReportJson(row: MockDmarcReport): DmarcReport {
  const { records: _records, ...rest } = row;
  return rest;
}

function ipv4ToInt(ip: string): number | null {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4 || parts.some((p) => Number.isNaN(p) || p < 0 || p > 255)) return null;
  return ((parts[0]! << 24) | (parts[1]! << 16) | (parts[2]! << 8) | parts[3]!) >>> 0;
}

/** IPv4-only best-effort CIDR match — good enough for mock fixtures (real matching, including IPv6, lives in packages/core/src/dmarc/senderClassification.ts). */
function ipInCidrMock(ip: string, cidr: string): boolean {
  const [base, prefixRaw] = cidr.split('/');
  const prefix = Number(prefixRaw ?? 32);
  const baseInt = base ? ipv4ToInt(base) : null;
  const ipInt = ipv4ToInt(ip);
  if (baseInt === null || ipInt === null || Number.isNaN(prefix)) return false;
  if (prefix === 0) return true;
  const mask = (~0 << (32 - prefix)) >>> 0;
  return (baseInt & mask) === (ipInt & mask);
}

function classifyMockSender(rec: DmarcRecord): string | null {
  for (const sender of dmarcKnownSenderStore.values()) {
    if (
      sender.matchType === 'dkim_domain' &&
      rec.dkimDomain &&
      rec.dkimDomain.toLowerCase() === sender.matchValue.toLowerCase()
    ) {
      return sender.label;
    }
    if (sender.matchType === 'ip_cidr' && ipInCidrMock(rec.sourceIp, sender.matchValue)) {
      return sender.label;
    }
  }
  return null;
}

/** Splits a CSV export's text into its status column's values (last cell of each data row is good enough for the mock — real parsing lives in packages/core/src/import). */
function csvStatusValues(csvText: string): string[] {
  const lines = csvText.split('\n').filter((l) => l.trim() !== '');
  const header = (lines[0] ?? '').split(',').map((h) => h.trim().toUpperCase());
  const statusIdx = header.findIndex((h) => h === 'STATUS');
  if (statusIdx === -1) return [];
  return lines.slice(1).map((line) => (line.split(',')[statusIdx] ?? '').trim().toLowerCase());
}

const listStore = new Map<string, MailingList>(mockLists.map((l) => [l.id, l]));
const senderStore = new Map<string, Sender>(mockSenders.map((s) => [s.id, s]));
const segmentStore = new Map<string, Segment>(mockSegments.map((s) => [s.id, s]));
/** Shared blocks (K4) for mock mode; starts empty, like a fresh tenant. */
const sharedBlockStore = new Map<string, SharedBlock>();

/** Neutral example products for the product feed block; the catalogue starts empty until "Refresh from shop". */
const MOCK_CATALOG_PRODUCTS: CatalogProduct[] = [1, 2, 3, 4, 5].map((n) => ({
  id: String(n),
  name: `Example product ${n}`,
  price: `${n * 10}.00`,
  regularPrice: `${n * 10 + (n === 2 ? 5 : 0)}.00`,
  salePrice: n === 2 ? `${n * 10}.00` : '',
  currency: 'EUR',
  imageUrl: null,
  permalink: `https://shop.example/product/${n}`,
  stockStatus: n === 4 ? 'outofstock' : 'instock',
  shopRank: n - 1,
  soldRecent: n === 3 ? 12 : n,
}));
const mockCatalog: { products: CatalogProduct[]; refreshedAt: number | null } = {
  products: [],
  refreshedAt: null,
};

/** Fase-4 warm-up admin state per sender, mutable so start/pause/reset stick within a session. */
const warmupStore = new Map<string, WarmupAdmin>();
/** Restores the seeded warm-up state, so tests that start, pause or restart a plan don't leak into each other. */
export function resetMockWarmup(): void {
  warmupStore.clear();
  for (const [id, w] of Object.entries(mockWarmupAdmin))
    warmupStore.set(id, { ...w, plan: [...w.plan] });
}
resetMockWarmup();
const NOT_STARTED_WARMUP: WarmupAdmin = {
  dailyLimit: 200,
  active: false,
  paused: false,
  target: null,
  startedAt: null,
  dayOffset: 0,
  limitToday: 200,
  plan: [],
};
const domainStore = new Map<string, SenderDomain>(mockSenderDomains.map((d) => [d.id, d]));
const audienceStore = new Map<string, CampaignAudience>([
  [
    'c-okt',
    {
      listIds: ['l-nyhedsbrev'],
      includeLabels: [],
      excludeLabels: [],
      includeSegmentIds: [],
      excludeSegmentIds: [],
      excludeOrderedSince: null,
    },
  ],
]);

function normalizeAudience(input: unknown): CampaignAudience {
  const a = (input ?? {}) as Partial<CampaignAudience>;
  return {
    listIds: a.listIds ?? [],
    includeLabels: a.includeLabels ?? [],
    excludeLabels: a.excludeLabels ?? [],
    includeSegmentIds: a.includeSegmentIds ?? [],
    excludeSegmentIds: a.excludeSegmentIds ?? [],
    excludeOrderedSince: a.excludeOrderedSince ?? null,
  };
}

/** Loose mirror of `packages/core/src/db/tenant/segments.repo.ts#validateSegmentFilter` — enough for mock CRUD, not a full re-validation. Accepts the old flat shape and v2. */
function normalizeSegmentFilter(input: unknown): SegmentFilter {
  const raw = (typeof input === 'object' && input !== null ? input : {}) as Record<string, unknown>;
  const rawGroups = Array.isArray(raw.groups) ? (raw.groups as unknown[]) : [raw];
  const groups = rawGroups.slice(0, 5).map(normalizeSegmentGroup);
  return { version: 2, groups: groups.length > 0 ? groups : [normalizeSegmentGroup({})] };
}

function normalizeSegmentGroup(input: unknown): SegmentGroup {
  const raw = (typeof input === 'object' && input !== null ? input : {}) as Record<string, unknown>;
  const list = (v: unknown): string[] => (Array.isArray(v) ? [...new Set(v as string[])] : []);
  const group: SegmentGroup = {
    labels: list(raw.labels),
    excludeLabels: list(raw.excludeLabels),
    fields: Array.isArray(raw.fields) ? (raw.fields as SegmentGroup['fields']) : [],
    unsubscribed: raw.unsubscribed === true,
    sources: list(raw.sources),
    languages: list(raw.languages),
    countries: list(raw.countries),
    excludeCountries: list(raw.excludeCountries),
    engagement: normalizeEngagement(raw.engagement),
  };
  if (Array.isArray(raw.consentCategories) && raw.consentCategories.length > 0) {
    group.consentCategories = list(raw.consentCategories) as SegmentGroup['consentCategories'] & [];
  }
  if (Array.isArray(raw.events) && raw.events.length > 0) {
    group.events = raw.events as SegmentEventCondition[];
  }
  if (Array.isArray(raw.purchases) && raw.purchases.length > 0) {
    group.purchases = raw.purchases as SegmentPurchaseCondition[];
  }
  if (Array.isArray(raw.predictions) && raw.predictions.length > 0) {
    group.predictions = raw.predictions as SegmentPredictionCondition[];
  }
  return group;
}

function normalizeEngagement(v: unknown): SegmentGroup['engagement'] {
  if (typeof v !== 'object' || v === null) return null;
  const e = v as Record<string, unknown>;
  if (e.kind !== 'clicked_within' && e.kind !== 'not_clicked_within') return null;
  const days = typeof e.days === 'number' ? Math.min(730, Math.max(1, Math.round(e.days))) : 90;
  if (e.kind === 'clicked_within') return { kind: e.kind, days };
  const minSends =
    typeof e.minSends === 'number' ? Math.min(100, Math.max(1, Math.round(e.minSends))) : 3;
  return { kind: e.kind, days, minSends };
}

function segmentGroupConditionCount(group: SegmentGroup): number {
  return (
    group.labels.length +
    group.excludeLabels.length +
    group.fields.length +
    (group.unsubscribed ? 1 : 0) +
    (group.sources.length > 0 ? 1 : 0) +
    (group.languages.length > 0 ? 1 : 0) +
    (group.countries.length > 0 ? 1 : 0) +
    (group.excludeCountries.length > 0 ? 1 : 0) +
    (group.engagement ? 1 : 0) +
    ((group.consentCategories?.length ?? 0) > 0 ? 1 : 0) +
    (group.events?.length ?? 0) +
    (group.purchases?.length ?? 0) +
    (group.predictions?.length ?? 0)
  );
}

function segmentConditionCount(filter: SegmentFilter): number {
  return filter.groups.reduce((sum, g) => sum + segmentGroupConditionCount(g), 0);
}

const MOCK_LIBRARY: SegmentLibraryResponse = {
  goals: [
    { goal: 'engagement', title: 'Increase engagement' },
    { goal: 'deliverability', title: 'Improve deliverability' },
    { goal: 'purchasers', title: 'Target past purchasers' },
    { goal: 'convert', title: 'Convert customers' },
    { goal: 'consent', title: 'Understand consent' },
  ],
  presets: [
    {
      key: 'engaged-30',
      goal: 'engagement',
      name: 'Engaged, last 30 days',
      description: 'Clicked a mail in the last 30 days.',
      filter: normalizeSegmentFilter({ engagement: { kind: 'clicked_within', days: 30 } }),
    },
    {
      key: 'unengaged-90',
      goal: 'deliverability',
      name: 'Unengaged, 90 days',
      description: 'No click in 90 days although they received at least 3 mails.',
      filter: normalizeSegmentFilter({
        engagement: { kind: 'not_clicked_within', days: 90, minSends: 3 },
      }),
    },
    {
      key: 'repeat-buyers',
      goal: 'purchasers',
      name: 'Repeat buyers',
      description: 'Two or more valid orders.',
      filter: normalizeSegmentFilter({
        events: [{ event: 'ordered', op: 'at_least', count: 2 }],
      }),
    },
    {
      key: 'never-bought',
      goal: 'convert',
      name: 'Subscribers who never bought',
      description: 'On the list but without a valid order.',
      filter: normalizeSegmentFilter({
        events: [{ event: 'ordered', op: 'at_most', count: 0 }],
      }),
    },
    {
      key: 'consent-double-optin',
      goal: 'consent',
      name: 'Consent: confirmed (double opt-in)',
      description: 'Confirmed their address by mail.',
      filter: normalizeSegmentFilter({ consentCategories: ['confirmed_double_optin'] }),
    },
  ],
};

/** Rough stand-in for the server estimate: list members, minus ~10 % per narrowing rule. */
function estimateMockAudience(audience: CampaignAudience): AudienceEstimate {
  const listMembers = audience.listIds.reduce(
    (sum, id) => sum + (listStore.get(id)?.confirmedCount ?? 0),
    0,
  );
  const rules =
    audience.includeLabels.length +
    audience.excludeLabels.length +
    audience.includeSegmentIds.length +
    audience.excludeSegmentIds.length +
    (audience.excludeOrderedSince !== null ? 1 : 0);
  return {
    listMembers,
    estimatedRecipients: Math.round(listMembers * 0.95 * 0.9 ** rules),
    heldByFrequencyCap: 0,
  };
}

function withAudience(campaign: Campaign): Campaign {
  const audience = normalizeAudience(audienceStore.get(campaign.id));
  return { ...campaign, audience, audienceEstimate: estimateMockAudience(audience) };
}

/** Mirrors `templates.repo.ts#getTemplateUsage` — counts campaigns (any status) and current-version automation steps referencing each template id. */
function templateUsage(id: string): { campaigns: number; automations: number } {
  const campaigns = Array.from(campaignStore.values()).filter((c) => c.templateId === id).length;
  const automations = Array.from(automationStore.values()).filter((a) => {
    const versions = automationVersionStore.get(a.id) ?? [];
    const current =
      versions.find((v) => v.version === a.currentVersion) ?? versions[versions.length - 1];
    return (current?.definition.steps ?? []).some((s) => {
      if (s.type !== 'send_template') return false;
      const variants = Array.isArray(s.variants) ? (s.variants as FlowLanguageVariant[]) : [];
      return s.templateId === id || variants.some((v) => v.templateId === id);
    });
  }).length;
  return { campaigns, automations };
}

function templateInUseMessage(id: string): string | null {
  const usedByNonSentCampaign = Array.from(campaignStore.values()).some(
    (c) => c.templateId === id && c.status !== 'sent',
  );
  const usedByAutomation = templateUsage(id).automations > 0;
  if (usedByNonSentCampaign || usedByAutomation) {
    return 'The template cannot be deleted because it is used by a campaign that has not been sent, or by an active automation.';
  }
  return null;
}

const SAMPLE_MERGE_VALUES: Record<string, string> = {
  first_name: 'Anna',
  coupon_code: 'EKSEMPEL10',
  unsubscribe_url: 'https://eksempel.dk/afmeld/preview',
  shop_url: 'https://eksempel.dk',
};

function fillSampleMergeFields(html: string): string {
  return html.replace(
    /\{\{\s*([a-z_]+)\s*\}\}/g,
    (m, key: string) => SAMPLE_MERGE_VALUES[key] ?? m,
  );
}

function readBody(init: RequestInit): unknown {
  if (!init.body) return undefined;
  try {
    return JSON.parse(String(init.body));
  } catch {
    return undefined;
  }
}

export async function mockFetch(path: string, init: RequestInit = {}): Promise<Response> {
  await new Promise((r) => setTimeout(r, 60)); // feel like a network
  const method = (init.method ?? 'GET').toUpperCase();
  const [pathname = '', search] = path.split('?');
  const params = new URLSearchParams(search ?? '');
  const body = readBody(init);

  const passkeysResponse = handleMockPasskeys(method, pathname, body, mockWindowStepUp);
  if (passkeysResponse) return passkeysResponse;

  // --- auth ---
  if (pathname === '/auth/login' && method === 'POST') {
    return json({ status: 'totp_required' });
  }
  if (pathname === '/auth/totp' && method === 'POST') {
    const totpBody = body as { code?: string; recoveryCode?: string } | undefined;
    if ((totpBody?.code ?? totpBody?.recoveryCode) === MOCK_LOCKING_CODE) {
      return json({ error: 'totp_locked' }, 429);
    }
    openMockStepUpWindow();
    return json({
      userId: 'u-mikkel',
      email: 'mikkel@example.com',
      tenantId: 't-1',
      role: 'admin',
      totpEnabled: true,
      csrfToken: 'mock-csrf-token',
      stepUpUntil: mockStepUpUntil,
    });
  }
  if (pathname === '/auth/me' && method === 'GET') {
    return json({
      userId: 'u-mikkel',
      email: 'mikkel@example.com',
      tenantId: 't-1',
      role: mockRole,
      totpEnabled: mockTotpEnabled,
      displayName: mockDisplayName,
      csrfToken: 'mock-csrf-token',
      stepUpUntil:
        mockStepUpUntil !== null && mockStepUpUntil > Date.now() ? mockStepUpUntil : null,
      deniedCapabilities: mockOwnDeniedCapabilities(),
    });
  }
  if (pathname === '/auth/me' && method === 'PATCH') {
    const name = (body as { displayName?: string | null } | undefined)?.displayName;
    if (typeof name === 'string' && name.length > 60) {
      return json({ error: 'invalid_request' }, 400);
    }
    mockDisplayName = name?.trim() || null;
    return json({ displayName: mockDisplayName });
  }
  if (pathname === '/auth/totp/enroll/begin' && method === 'POST') {
    return json({
      secretBase32: 'JBSWY3DPEHPK3PXP',
      otpauthUri: 'otpauth://totp/NoPain:mikkel@example.com?secret=JBSWY3DPEHPK3PXP',
    });
  }
  if (pathname === '/auth/totp/enroll/confirm' && method === 'POST') {
    if ((body as { code?: string } | undefined)?.code !== '123456') {
      return json({ error: 'invalid_code' }, 401);
    }
    mockTotpEnabled = true;
    return json({ status: 'confirmed', recoveryCodes: ['aaaa-bbbb', 'cccc-dddd'] });
  }
  if (pathname === '/auth/logout' && method === 'POST') {
    return json({ status: 'success' });
  }
  // Public redeem of an admin-issued access-reset link (mirrors
  // `POST /api/auth/reset-access`, `apps/app/src/routes/auth.ts`) —
  // pre-session, no mockRole/auth check.
  if (pathname === '/auth/reset-access' && method === 'POST') {
    const b = (body ?? {}) as { token?: string; newPassword?: string };
    const token = b.token ?? '';
    const targetId = [...mockResetTokens.entries()].find(([, t]) => t === token)?.[0];
    if (!targetId) return json({ error: 'invalid_or_expired_token' }, 400);
    if (!b.newPassword || b.newPassword.length < 12) {
      return json({ error: 'password_too_short' }, 400);
    }
    // Single-use, like the real atomic consume: the second redeem of the
    // same token fails from here on.
    mockResetTokens.delete(targetId);
    const target = userStore.get(targetId);
    if (target) userStore.set(targetId, { ...target, totpEnabled: false });
    return json({ status: 'success' });
  }

  // Public accept of an invitation (mirrors `POST /api/invites/accept`).
  // Tokens: `mock-invite-<id>`; an expired one is `MOCK_EXPIRED_INVITE_TOKEN`.
  if (pathname === '/invites/accept' && method === 'POST') {
    const b = (body ?? {}) as { token?: string; password?: string; totpCode?: string };
    const entry = mockInvites.get(b.token ?? '');
    if (!entry || entry.invitation.acceptedAt !== null || entry.invitation.expiresAt < Date.now()) {
      return json({ error: 'invalid_or_expired_token' }, 400);
    }
    const { invitation } = entry;
    const existing = Array.from(userStore.values()).find((u) => u.email === invitation.email);
    if (existing) {
      if (!b.password) return json({ error: 'existing_account_auth_required' }, 401);
      if (b.password === MOCK_RATE_LIMITED_PASSWORD)
        return json({ error: 'invalid_credentials' }, 429);
      if (b.password !== MOCK_EXISTING_PASSWORD) return json({ error: 'invalid_credentials' }, 401);
      if (b.totpCode === undefined && existing.totpEnabled)
        return json({ error: 'totp_required' }, 401);
      if (b.totpCode !== undefined && b.totpCode !== '123456') {
        return json(
          { error: b.totpCode === MOCK_LOCKING_CODE ? 'totp_locked' : 'invalid_code' },
          b.totpCode === MOCK_LOCKING_CODE ? 429 : 401,
        );
      }
      invitation.acceptedAt = Date.now();
      return json({ status: 'membership_added', tenantId: 't-1', role: invitation.role });
    }
    if (!b.password || b.password.length < 12) return json({ error: 'password_too_short' }, 400);
    if (b.password === MOCK_PWNED_PASSWORD)
      return json({ error: 'password_pwned', breachCount: 1234 }, 400);
    invitation.acceptedAt = Date.now();
    const created: TenantUser = {
      id: `u-${nextId++}`,
      email: invitation.email,
      role: invitation.role,
      totpEnabled: false,
      disabledAt: null,
      createdAt: Date.now(),
    };
    userStore.set(created.id, created);
    // A new user has no 2FA yet: `/auth/me` now reports that, so the app leads into 2FA setup.
    mockTotpEnabled = false;
    return json({
      status: 'success',
      userId: created.id,
      email: created.email,
      tenantId: 't-1',
      role: created.role,
      memberships: [],
      totpEnabled: false,
      csrfToken: 'mock-csrf-token',
    });
  }

  // --- health ---
  if (pathname === '/health-summary' && method === 'GET') {
    return json({
      ...mockHealthSummary,
      ...(healthAttempted == null
        ? {}
        : {
            bounce: { ...mockHealthSummary.bounce, rate: 0.056, attempted: healthAttempted },
            complaint: { ...mockHealthSummary.complaint, rate: 0.056, attempted: healthAttempted },
          }),
      ...(healthWarmup === undefined ? {} : { warmup: healthWarmup }),
      killSwitch: { engaged: killSwitchEngaged },
      senderPaused,
    });
  }

  // --- senders (same rules as apps/app/src/routes/senders.ts) ---
  const resumeMatch = /^\/senders\/([^/]+)\/resume$/.exec(pathname);
  if (resumeMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    if (!senderPaused.paused || senderPaused.senderId !== resumeMatch[1]) {
      return json({ error: 'sender_not_paused', message: 'The sender is not stopped.' }, 409);
    }
    if (senderPaused.kind !== 'ses_halt' && senderPaused.kind !== 'auto_pause') {
      return json(
        {
          error: 'pause_not_recognized',
          message:
            'The reason for the stop could not be recognized. See the campaign and runbook section 3.',
        },
        409,
      );
    }
    const reqBody = body as { totpCode?: string; acknowledge?: string } | undefined;
    if (
      senderPaused.kind === 'auto_pause' &&
      reqBody?.acknowledge?.trim().toUpperCase() !== 'RESUME'
    ) {
      return json(
        {
          error: 'acknowledgement_required',
          message: 'Type "RESUME" to confirm that the bounce or complaint rate is accepted.',
        },
        409,
      );
    }
    const stepUpError = mockWindowStepUp(reqBody ?? {});
    if (stepUpError) return stepUpError;
    senderPaused = mockHealthSummary.senderPaused;
    return json({ status: 'success', killSwitchEngaged });
  }

  // --- warm-up (fase-4; mirrors apps/app/src/routes/warmup.ts) ---
  const warmupMatch = /^\/senders\/([^/]+)\/warmup$/.exec(pathname);
  if (warmupMatch && method === 'GET') {
    const senderId = warmupMatch[1] as string;
    return json(warmupStore.get(senderId) ?? NOT_STARTED_WARMUP);
  }
  const warmupStartMatch = /^\/senders\/([^/]+)\/warmup\/start$/.exec(pathname);
  if (warmupStartMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const senderId = warmupStartMatch[1] as string;
    const b = (body ?? {}) as {
      totpCode?: string;
      target?: number;
      dailyLimit?: number;
      startLimit?: number;
    };
    const stepUpError = mockWindowStepUp(b);
    if (stepUpError) return stepUpError;
    const dailyLimit = b.dailyLimit ?? (warmupStore.get(senderId) ?? NOT_STARTED_WARMUP).dailyLimit;
    const target = Math.min(b.target ?? 25_144, dailyLimit);
    // Same curve as core's generateWarmupCurve: ×1.5 every second day from the first-day limit.
    let current = Math.min(b.startLimit ?? 200, target);
    const plan = [{ dayOffset: 0, maxSends: current }];
    while (current < target) {
      current = Math.min(target, Math.round(current * 1.5));
      plan.push({ dayOffset: plan.length * 2, maxSends: current });
    }
    const started: WarmupAdmin = {
      dailyLimit,
      active: true,
      paused: false,
      target,
      startedAt: Date.now(),
      dayOffset: 0,
      limitToday: plan[0]?.maxSends ?? 200,
      plan,
    };
    warmupStore.set(senderId, started);
    return json(started);
  }
  const warmupPauseMatch = /^\/senders\/([^/]+)\/warmup\/pause$/.exec(pathname);
  if (warmupPauseMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const senderId = warmupPauseMatch[1] as string;
    const state = warmupStore.get(senderId);
    if (!state || !state.active) {
      return json({ error: 'warmup_not_started', message: 'Warm-up has not been started.' }, 409);
    }
    const updated: WarmupAdmin = { ...state, paused: true };
    warmupStore.set(senderId, updated);
    return json(updated);
  }
  const warmupResetMatch = /^\/senders\/([^/]+)\/warmup\/reset$/.exec(pathname);
  if (warmupResetMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const senderId = warmupResetMatch[1] as string;
    const state = warmupStore.get(senderId);
    if (!state || !state.active) {
      return json({ error: 'warmup_not_started', message: 'Warm-up has not been started.' }, 409);
    }
    const updated: WarmupAdmin = {
      ...state,
      paused: false,
      startedAt: Date.now(),
      dayOffset: 0,
      limitToday: state.plan[0]?.maxSends ?? 200,
    };
    warmupStore.set(senderId, updated);
    return json(updated);
  }

  // --- NP-Flow Woo plugin settings (apps/app/src/routes/wooSettings.ts) ---
  if (pathname === '/settings/tracking' && method === 'GET') {
    return json(trackingSettings);
  }
  if (pathname === '/settings/tracking' && method === 'PUT') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const input = body as { enabled?: unknown; lowStockThreshold?: unknown } | undefined;
    if (!input || typeof input.enabled !== 'boolean') {
      return json({ error: 'invalid_request', message: 'enabled must be true or false.' }, 400);
    }
    const threshold = input.lowStockThreshold;
    if (
      threshold !== undefined &&
      (typeof threshold !== 'number' ||
        !Number.isInteger(threshold) ||
        threshold < 1 ||
        threshold > 1000)
    ) {
      return json(
        { error: 'invalid_request', message: 'Enter a whole number from 1 to 1000.' },
        400,
      );
    }
    trackingSettings = {
      ...trackingSettings,
      enabled: input.enabled,
      lowStockThreshold: threshold ?? trackingSettings.lowStockThreshold,
      updatedAt: Date.now(),
    };
    return json(trackingSettings);
  }
  if (pathname === '/settings/popup' && method === 'GET') {
    return json(popupSettings);
  }
  if (pathname === '/settings/popup' && method === 'PUT') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const input = body as Partial<PopupSettings> | undefined;
    if (!input || typeof input.headline !== 'string' || input.headline.trim() === '') {
      return json({ error: 'invalid_request', message: 'The headline must not be empty.' }, 400);
    }
    popupSettings = {
      ...popupSettings,
      ...input,
      embedCode: popupSettings.embedCode,
      updatedAt: Date.now(),
    } as PopupSettings;
    return json(popupSettings);
  }
  if (pathname === '/settings/sending-cost' && method === 'GET') {
    return json(sendCostSetting);
  }
  if (pathname === '/settings/sending-cost' && method === 'PUT') {
    const value = (body as { costPerThousand?: unknown } | undefined)?.costPerThousand;
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100) {
      return json({ error: 'invalid_request', message: 'Enter an amount from 0 to 100.' }, 400);
    }
    sendCostSetting = { ...sendCostSetting, costPerThousand: value, updatedAt: Date.now() };
    return json(sendCostSetting);
  }
  if (pathname === '/settings/woo' && method === 'GET') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    return json(wooStatus);
  }
  if (pathname === '/settings/woo' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as {
      pluginUrl?: string;
      secret?: string;
      totpCode?: string;
    };
    const stepUpError = mockWindowStepUp(b);
    if (stepUpError) return stepUpError;
    if (!b.pluginUrl || !/^https:\/\//.test(b.pluginUrl)) {
      return json(
        { error: 'invalid_woo_key', message: 'Plugin URL must start with https://.' },
        400,
      );
    }
    if (!b.secret || b.secret.length < 16) {
      return json(
        { error: 'invalid_woo_key', message: 'The shared secret must be at least 16 characters.' },
        400,
      );
    }
    wooStatus = { configured: true, pluginUrl: b.pluginUrl, lastRotatedAt: Date.now() };
    return json(wooStatus);
  }
  if (pathname === '/settings/woo/webhook' && method === 'GET') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    return json(wooWebhookStatus);
  }
  if (pathname === '/settings/woo/webhook' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { secret?: string; totpCode?: string };
    const stepUpError = mockWindowStepUp(b);
    if (stepUpError) return stepUpError;
    if (!b.secret || b.secret.trim().length < 16 || b.secret.trim().length > 200) {
      return json(
        {
          error: 'invalid_webhook_secret',
          message: 'The webhook secret could not be saved. It must be 16 to 200 characters.',
        },
        400,
      );
    }
    wooWebhookStatus = { ...wooWebhookStatus, configured: true, lastRotatedAt: Date.now() };
    return json(wooWebhookStatus);
  }
  if (pathname === '/back-in-stock' && method === 'GET') {
    return json({
      settings: mockBackInStock,
      pending: [
        { productId: '10', variationId: '', productName: 'NoPain Numbing Cream', pending: 4 },
        { productId: '11', variationId: '', productName: 'NoPain Aftercare Balm', pending: 1 },
      ],
      pendingTotal: 5,
    });
  }
  if (pathname === '/back-in-stock/preview' && method === 'GET') {
    const subject = mockBackInStock.subject ?? 'NoPain Numbing Cream is back in stock';
    return json({
      language: 'en',
      subject,
      html: '<html><body><p>' + subject + '</p></body></html>',
      text: subject,
    });
  }
  if (pathname === '/back-in-stock' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as {
      enabled?: boolean;
      subject?: string | null;
      bodyText?: string | null;
      totpCode?: string;
    };
    if (b.enabled !== undefined && b.enabled !== mockBackInStock.enabled) {
      const stepUpError = mockWindowStepUp(b);
      if (stepUpError) return stepUpError;
    }
    mockBackInStock = {
      enabled: b.enabled ?? mockBackInStock.enabled,
      subject: b.subject === undefined ? mockBackInStock.subject : b.subject,
      bodyText: b.bodyText === undefined ? mockBackInStock.bodyText : b.bodyText,
      updatedAt: Date.now(),
    };
    return json(mockBackInStock);
  }
  if (pathname === '/settings/woo/order-signup' && method === 'GET') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    return json(wooOrderSignupStatus);
  }
  if (pathname === '/settings/woo/order-signup' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { enabled?: boolean; totpCode?: string };
    const stepUpError = mockWindowStepUp(b);
    if (stepUpError) return stepUpError;
    if (typeof b.enabled !== 'boolean') return json({ error: 'invalid_request' }, 400);
    const now = Date.now();
    wooOrderSignupStatus = {
      enabled: b.enabled,
      enabledAt: b.enabled && !wooOrderSignupStatus.enabled ? now : wooOrderSignupStatus.enabledAt,
      updatedAt: now,
    };
    return json(wooOrderSignupStatus);
  }
  if (pathname === '/settings/woo/test-connection' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    if (!wooStatus.configured) return json({ error: 'not_configured' }, 409);
    if (!wooTestConnectionOk) return json({ error: 'woo_connection_failed' }, 502);
    return json({ status: 'success' });
  }

  // --- users + admin-udløst 2FA/adgangskode-nulstilling (apps/app/src/routes/users.ts) ---
  if (pathname === '/users' && method === 'GET') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    return json({ users: withCustomRoles(Array.from(userStore.values())) });
  }
  const customRolesResponse = handleMockCustomRoles(method, pathname, body, mockRole, userStore);
  if (customRolesResponse) return customRolesResponse;
  // Admin-triggered reset of a member's access (redesigned 2026-09-29):
  // mirrors `POST /api/users/:id/reset-access` — never returns a token,
  // only `{status:'sent'}`. The token is held here only so the public
  // `/auth/reset-access` mock above can redeem it, same as the target
  // would from the mailed link.
  const resetAccessMatch = /^\/users\/([^/]+)\/reset-access$/.exec(pathname);
  if (resetAccessMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const targetId = resetAccessMatch[1] as string;
    const b = (body ?? {}) as { totpCode?: string };
    const stepUpError = mockWindowStepUp(b, MOCK_MAIL_FAILED_CODE);
    if (stepUpError) return stepUpError;
    if (targetId === 'u-mikkel') return json({ error: 'cannot_reset_self' }, 400);
    const target = userStore.get(targetId);
    if (!target) return json({ error: 'not_found' }, 404);
    const token = `mock-reset-${targetId}-${Date.now()}`;
    mockResetTokens.set(targetId, token);
    return json({ status: 'sent' });
  }

  // --- API tokens (apps/app/src/routes/apiTokens.ts): admin-only, 404 when the flag is off ---
  if (pathname === '/api-tokens' || pathname.startsWith('/api-tokens/')) {
    if (!mockApiTokensEnabled) return json({ error: 'not_found' }, 404);
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    if (pathname === '/api-tokens' && method === 'GET') {
      return json({
        tokens: Array.from(mockApiTokens.values()).reverse(),
        productionScope: mockApiTokensProductionScope,
      });
    }
    if (pathname === '/api-tokens' && method === 'POST') {
      const b = (body ?? {}) as {
        name?: string;
        userId?: string;
        role?: string;
        expiresInDays?: number;
        totpCode?: string;
      };
      const member = b.userId ? userStore.get(b.userId) : undefined;
      if (!b.name?.trim() || !b.totpCode || !['editor', 'viewer'].includes(b.role ?? '')) {
        return json({ error: 'invalid_request' }, 400);
      }
      // Always a fresh code (the window is ignored), like the export and the GDPR delete.
      const stepUpError = mockStepUpError(b);
      if (stepUpError) return stepUpError;
      if (!member) return json({ error: 'not_found' }, 404);
      const days = b.expiresInDays ?? 90;
      const now = Date.now();
      const id = `tok-${nextId++}`;
      const row: ApiTokenRow = {
        id,
        name: b.name.trim(),
        userId: member.id,
        userEmail: member.email,
        role: b.role as 'editor' | 'viewer',
        createdAt: now,
        expiresAt: now + days * 86_400_000,
        lastUsedAt: null,
        revokedAt: null,
      };
      mockApiTokens.set(id, row);
      return json(
        {
          token: `npf_mock${id}secret`,
          apiToken: {
            id,
            name: row.name,
            userId: row.userId,
            role: row.role,
            expiresAt: row.expiresAt,
          },
        },
        201,
      );
    }
    const revokeMatch = /^\/api-tokens\/([^/]+)\/revoke$/.exec(pathname);
    if (revokeMatch && method === 'POST') {
      const row = mockApiTokens.get(revokeMatch[1] as string);
      if (!row) return json({ error: 'not_found' }, 404);
      if (row.revokedAt === null) row.revokedAt = Date.now();
      return json({ status: 'revoked' });
    }
  }

  // --- invitations (apps/app/src/routes/invites.ts) ---
  if (pathname === '/invites' && method === 'GET') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    return json({ invitations: Array.from(mockInvites.values()).map((i) => i.invitation) });
  }
  if (pathname === '/invites' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { email?: string; role?: Role };
    if (
      !b.email ||
      !b.email.includes('@') ||
      !['admin', 'editor', 'viewer'].includes(b.role ?? '')
    ) {
      return json({ error: 'invalid_request' }, 400);
    }
    const now = Date.now();
    const id = `inv-${nextId++}`;
    const token = `mock-invite-${id}`;
    const invitation: Invitation = {
      id,
      email: b.email,
      role: b.role as Role,
      expiresAt: now + 7 * 86_400_000,
      acceptedAt: null,
      createdAt: now,
    };
    mockInvites.set(token, { invitation });
    return json({
      status: 'success',
      invitationId: id,
      email: invitation.email,
      role: invitation.role,
      expiresAt: invitation.expiresAt,
      inviteToken: token,
    });
  }

  // --- sessions (apps/app/src/routes/session.ts) ---
  if (pathname === '/session/revoke-all' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { totpCode?: string };
    const stepUpError = mockWindowStepUp(b);
    if (stepUpError) return stepUpError;
    return json({ status: 'success', revokedCount: 4 });
  }

  // --- lists, senders, sender domains, segments (apps/app/src/routes/{lists,senders,segments}.ts) ---
  const adminOnly = method !== 'GET' && mockRole !== 'admin';
  const includeArchived = params.get('includeArchived') === '1';

  if (pathname === '/lists' && method === 'GET') {
    return json({
      items: Array.from(listStore.values()).filter((l) => includeArchived || !l.archived),
    });
  }
  if (pathname === '/lists' && method === 'POST') {
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as {
      name?: string;
      description?: string | null;
      doubleOptin?: boolean;
      isPublic?: boolean;
    };
    const name = (b.name ?? '').trim();
    if (!name)
      return json({ error: 'invalid_request', message: 'The list must have a name.' }, 400);
    const list: MailingList = {
      id: `l-${nextId++}`,
      name,
      description: b.description?.trim() || null,
      doubleOptin: b.doubleOptin ?? false,
      archived: false,
      archivedAt: null,
      isPublic: b.isPublic ?? false,
      isDefaultSignup: false,
      confirmedCount: 0,
      campaignCount: 0,
      createdAt: Date.now(),
      updatedAt: null,
    };
    listStore.set(list.id, list);
    return json(list, 201);
  }
  const listDefaultSignupMatch = /^\/lists\/([^/]+)\/default-signup$/.exec(pathname);
  if (listDefaultSignupMatch && method === 'PATCH') {
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    const id = listDefaultSignupMatch[1] as string;
    const b = (body ?? {}) as { clear?: boolean };
    if (b.clear) {
      for (const [key, l] of listStore) {
        if (l.isDefaultSignup) listStore.set(key, { ...l, isDefaultSignup: false });
      }
      return json({ result: 'cleared' });
    }
    const target = listStore.get(id);
    if (!target) return json({ error: 'not_found' }, 404);
    for (const [key, l] of listStore) {
      if (l.isDefaultSignup) listStore.set(key, { ...l, isDefaultSignup: false });
    }
    const updated: MailingList = { ...target, isDefaultSignup: true };
    listStore.set(id, updated);
    return json(updated);
  }
  const listMatch = /^\/lists\/([^/]+)$/.exec(pathname);
  if (listMatch) {
    const list = listStore.get(listMatch[1] as string);
    if (!list) return json({ error: 'not_found' }, 404);
    if (method === 'GET') return json(list);
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    if (method === 'PATCH') {
      const b = (body ?? {}) as Partial<MailingList>;
      const updated: MailingList = {
        ...list,
        ...(b.name !== undefined ? { name: b.name.trim() } : {}),
        ...(b.description !== undefined ? { description: b.description } : {}),
        ...(b.doubleOptin !== undefined ? { doubleOptin: b.doubleOptin } : {}),
        ...(b.isPublic !== undefined ? { isPublic: b.isPublic } : {}),
        ...(b.archived !== undefined
          ? { archived: b.archived, archivedAt: b.archived ? Date.now() : null }
          : {}),
        updatedAt: Date.now(),
      };
      if (!updated.name) {
        return json({ error: 'invalid_request', message: 'The list must have a name.' }, 400);
      }
      listStore.set(list.id, updated);
      return json(updated);
    }
    if (method === 'DELETE') {
      if (list.campaignCount > 0) {
        listStore.set(list.id, { ...list, archived: true, archivedAt: Date.now() });
        return json({ result: 'archived' });
      }
      listStore.delete(list.id);
      return json({ result: 'deleted' });
    }
  }

  if (pathname === '/sender-domains' && method === 'GET') {
    return json({
      items: Array.from(domainStore.values()).map((d) => ({
        ...d,
        senderCount: Array.from(senderStore.values()).filter(
          (s) => !s.archived && s.fromEmail.endsWith(`@${d.domain}`),
        ).length,
      })),
    });
  }
  if (pathname === '/sender-domains' && method === 'POST') {
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    const domain = String((body as { domain?: string } | undefined)?.domain ?? '')
      .trim()
      .toLowerCase();
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain)) {
      return json({ error: 'invalid_domain', message: 'The domain is not valid.' }, 400);
    }
    if (Array.from(domainStore.values()).some((d) => d.domain === domain)) {
      return json({ error: 'domain_exists', message: 'The domain already exists.' }, 409);
    }
    const created: SenderDomain = {
      id: `d-${nextId++}`,
      domain,
      status: 'pending',
      verificationMethod: null,
      verifiedAt: null,
      senderCount: 0,
      createdAt: Date.now(),
    };
    domainStore.set(created.id, created);
    return json(created, 201);
  }
  const domainMatch = /^\/sender-domains\/([^/]+)(\/verify)?$/.exec(pathname);
  if (domainMatch) {
    const domain = domainStore.get(domainMatch[1] as string);
    if (!domain) return json({ error: 'not_found' }, 404);
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    if (domainMatch[2] && method === 'POST') {
      const verified: SenderDomain = {
        ...domain,
        status: 'verified',
        verificationMethod: 'manual',
        verifiedAt: Date.now(),
      };
      domainStore.set(domain.id, verified);
      return json(verified);
    }
    if (!domainMatch[2] && method === 'DELETE') {
      const inUse = Array.from(senderStore.values()).some(
        (s) => !s.archived && s.fromEmail.endsWith(`@${domain.domain}`),
      );
      if (inUse) {
        return json(
          { error: 'domain_in_use', message: 'The domain is used by an active sender.' },
          409,
        );
      }
      domainStore.delete(domain.id);
      return json({ result: 'deleted' });
    }
  }

  if (pathname === '/senders' && method === 'GET') {
    return json({
      items: Array.from(senderStore.values()).filter((s) => includeArchived || !s.archived),
    });
  }
  if (pathname === '/senders' && method === 'POST') {
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as {
      fromName?: string;
      fromEmail?: string;
      replyToEmail?: string | null;
    };
    const fromEmail = (b.fromEmail ?? '').trim().toLowerCase();
    const fromName = (b.fromName ?? '').trim();
    if (!fromName) {
      return json({ error: 'invalid_name', message: 'The sender name must be filled in.' }, 400);
    }
    const domain = Array.from(domainStore.values()).find((d) => fromEmail.endsWith(`@${d.domain}`));
    if (domain?.status !== 'verified') {
      return json(
        {
          error: 'domain_not_verified',
          message: 'The sender address must be on a verified domain.',
        },
        400,
      );
    }
    const sender: Sender = {
      id: `s-${nextId++}`,
      fromName,
      fromEmail,
      replyToEmail: b.replyToEmail?.trim() || null,
      domainStatus: 'verified',
      dkimVerified: false,
      openTrackingEnabled: false,
      archived: false,
      archivedAt: null,
      campaignCount: 0,
      createdAt: Date.now(),
      updatedAt: null,
    };
    senderStore.set(sender.id, sender);
    return json(sender, 201);
  }
  const senderMatch = /^\/senders\/([^/]+)$/.exec(pathname);
  if (senderMatch) {
    const sender = senderStore.get(senderMatch[1] as string);
    if (!sender) return json({ error: 'not_found' }, 404);
    if (method === 'GET') return json(sender);
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    if (method === 'PATCH') {
      const b = (body ?? {}) as Partial<Sender>;
      const updated: Sender = {
        ...sender,
        ...(b.fromName !== undefined ? { fromName: b.fromName } : {}),
        ...(b.replyToEmail !== undefined ? { replyToEmail: b.replyToEmail } : {}),
        ...(b.openTrackingEnabled !== undefined
          ? { openTrackingEnabled: b.openTrackingEnabled }
          : {}),
        ...(b.archived !== undefined
          ? { archived: b.archived, archivedAt: b.archived ? Date.now() : null }
          : {}),
        updatedAt: Date.now(),
      };
      senderStore.set(sender.id, updated);
      return json(updated);
    }
    if (method === 'DELETE') {
      if (sender.campaignCount > 0) {
        senderStore.set(sender.id, { ...sender, archived: true, archivedAt: Date.now() });
        return json({ result: 'archived' });
      }
      senderStore.delete(sender.id);
      return json({ result: 'deleted' });
    }
  }

  // --- shared blocks (apps/app/src/routes/sharedBlocks.ts) ---
  if (pathname === '/shared-blocks' && method === 'GET') {
    return json({ items: Array.from(sharedBlockStore.values()) });
  }
  if (pathname === '/shared-blocks' && method === 'POST') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { name?: string; html?: string; blockJson?: string | null };
    const name = (b.name ?? '').trim();
    if (!name || !(b.html ?? '').trim()) {
      return json(
        { error: 'invalid_request', message: 'A name and some content are needed.' },
        400,
      );
    }
    if (
      Array.from(sharedBlockStore.values()).some((x) => x.name.toLowerCase() === name.toLowerCase())
    ) {
      return json(
        { error: 'name_taken', message: 'A shared block with this name already exists.' },
        409,
      );
    }
    const block: SharedBlock = {
      id: `sb-${nextId++}`,
      name,
      html: b.html ?? '',
      blockJson: b.blockJson ?? null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      usage: { templates: 0, draftCampaigns: 0 },
    };
    sharedBlockStore.set(block.id, block);
    return json(block, 201);
  }
  const sharedBlockMatch = /^\/shared-blocks\/([^/]+)$/.exec(pathname);
  if (sharedBlockMatch) {
    const block = sharedBlockStore.get(sharedBlockMatch[1] as string);
    if (!block) return json({ error: 'not_found' }, 404);
    if (method === 'GET') return json(block);
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    if (method === 'PUT') {
      const b = (body ?? {}) as { name?: string; html?: string };
      const next: SharedBlock = {
        ...block,
        name: b.name !== undefined ? b.name.trim() : block.name,
        html: b.html ?? block.html,
        updatedAt: Date.now(),
      };
      sharedBlockStore.set(block.id, next);
      return json(next);
    }
    if (method === 'DELETE') {
      sharedBlockStore.delete(block.id);
      return json({ result: 'deleted' });
    }
  }

  // --- product catalogue (apps/app/src/routes/catalog.ts) ---
  if (pathname === '/catalog' && method === 'GET') {
    return json({ products: mockCatalog.products, refreshedAt: mockCatalog.refreshedAt });
  }
  if (pathname === '/catalog/refresh' && method === 'POST') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    mockCatalog.products = MOCK_CATALOG_PRODUCTS;
    mockCatalog.refreshedAt = Date.now();
    return json({ products: mockCatalog.products, refreshedAt: mockCatalog.refreshedAt });
  }

  if (pathname === '/segments' && method === 'GET') {
    return json({
      items: Array.from(segmentStore.values()).filter((s) => includeArchived || !s.archived),
    });
  }
  if (pathname === '/segments/library' && method === 'GET') return json(MOCK_LIBRARY);
  if (pathname === '/segments/estimate' && method === 'POST') {
    const b = (body ?? {}) as { filter?: unknown };
    const filter = normalizeSegmentFilter(b.filter);
    const n = segmentConditionCount(filter);
    const matching = n === 0 ? 0 : Math.max(12, 4200 - n * 450);
    return json({ matching, reachable: Math.round(matching * 0.94) });
  }
  if (pathname === '/segments' && method === 'POST') {
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { name?: string; filter?: unknown };
    const name = (b.name ?? '').trim();
    if (!name) {
      return json({ error: 'invalid_request', message: 'The name must not be empty.' }, 400);
    }
    const filter = normalizeSegmentFilter(b.filter);
    if (segmentConditionCount(filter) === 0) {
      return json(
        { error: 'invalid_request', message: 'A segment must have at least one condition.' },
        400,
      );
    }
    const segment: Segment = {
      id: `seg-${nextId++}`,
      name,
      filter,
      archived: false,
      archivedAt: null,
      campaignCount: 0,
      createdAt: Date.now(),
      updatedAt: null,
    };
    segmentStore.set(segment.id, segment);
    return json(segment, 201);
  }
  const segmentMatch = /^\/segments\/([^/]+)$/.exec(pathname);
  if (segmentMatch) {
    const segment = segmentStore.get(segmentMatch[1] as string);
    if (!segment) return json({ error: 'not_found' }, 404);
    if (method === 'GET') return json(segment);
    if (adminOnly) return json({ error: 'forbidden' }, 403);
    if (method === 'PATCH') {
      const b = (body ?? {}) as { name?: string; filter?: unknown; archived?: boolean };
      const name = b.name !== undefined ? b.name.trim() : segment.name;
      if (!name) {
        return json({ error: 'invalid_request', message: 'The name must not be empty.' }, 400);
      }
      const filter =
        b.filter !== undefined
          ? normalizeSegmentFilter(b.filter)
          : (segment.filter ?? normalizeSegmentFilter(undefined));
      if (segmentConditionCount(filter) === 0) {
        return json(
          { error: 'invalid_request', message: 'A segment must have at least one condition.' },
          400,
        );
      }
      const updated: Segment = {
        ...segment,
        name,
        filter,
        ...(b.archived !== undefined
          ? {
              archived: b.archived,
              archivedAt: b.archived ? (segment.archivedAt ?? Date.now()) : null,
            }
          : {}),
        updatedAt: Date.now(),
      };
      segmentStore.set(segment.id, updated);
      return json(updated);
    }
    if (method === 'DELETE') {
      if (segment.campaignCount > 0) {
        segmentStore.set(segment.id, {
          ...segment,
          archived: true,
          archivedAt: segment.archivedAt ?? Date.now(),
        });
        return json({ result: 'archived' });
      }
      segmentStore.delete(segment.id);
      return json({ result: 'deleted' });
    }
  }

  if (pathname === '/campaigns/audience-estimate' && method === 'POST') {
    const audience = normalizeAudience(body);
    const unknown = audience.listIds.find((id) => !listStore.has(id));
    if (unknown) {
      return json(
        { error: 'unknown_list', message: 'One of the selected lists does not exist.' },
        400,
      );
    }
    return json(estimateMockAudience(audience));
  }

  if (pathname === '/campaigns/smart-send-coverage' && method === 'POST') {
    // Example numbers only: about two thirds of the audience have a usual hour.
    const audience = normalizeAudience(body);
    const recipients = estimateMockAudience(audience).estimatedRecipients;
    const withData = Math.floor((recipients * 2) / 3);
    return json({ recipients, withData, fallback: recipients - withData, minEvents: 3 });
  }

  // --- kill switch ---
  if (pathname === '/kill-switch' && method === 'GET') {
    return json({ engaged: killSwitchEngaged });
  }
  if (pathname === '/kill-switch/engage' && method === 'POST') {
    killSwitchEngaged = true;
    return json({ status: 'success' });
  }
  if (pathname === '/kill-switch/disengage' && method === 'POST') {
    const b = (body ?? {}) as { totpCode?: string; recoveryCode?: string };
    const stepUpError = mockWindowStepUp(b);
    if (stepUpError) return stepUpError;
    killSwitchEngaged = false;
    return json({ status: 'success' });
  }

  const customObjectsResponse = handleMockCustomObjects(method, pathname, body, mockRole);
  if (customObjectsResponse) return customObjectsResponse;

  const k8Response = handleMockK8(method, pathname, params, body, mockRole, mockWindowStepUp);
  if (k8Response) return k8Response;

  // --- campaigns ---
  if (pathname === '/campaigns' && method === 'GET') {
    const tag = params.get('tag');
    const all = Array.from(campaignStore.values()).map((c) => ({
      ...c,
      tags: mockCampaignTags(c.id),
    }));
    return json({ campaigns: tag ? all.filter((c) => c.tags.includes(tag)) : all });
  }
  if (pathname === '/campaigns' && method === 'POST') {
    const b = body as Partial<Campaign>;
    const sender = b.senderId ? senderStore.get(b.senderId) : undefined;
    if (!sender || sender.archived) {
      return json({ error: 'invalid_sender', message: 'Select an active sender.' }, 400);
    }
    // From a template the campaign gets a copy (html and block model); it never follows the template.
    const fromTemplate =
      b.html === undefined && b.templateId ? templateStore.get(b.templateId) : undefined;
    if (b.html === undefined && !fromTemplate) {
      return json(
        b.templateId
          ? { error: 'template_not_found', message: 'The template does not exist.' }
          : { error: 'invalid_request' },
        400,
      );
    }
    const campaign: Campaign = {
      id: `c-${nextId++}`,
      senderId: sender.id,
      templateId: b.templateId ?? null,
      name: b.name ?? 'Ny kampagne',
      subject: b.subject ?? '',
      preheader: b.preheader?.trim() || null,
      html: fromTemplate ? fromTemplate.html : (b.html ?? '<p></p>'),
      blockJson: fromTemplate
        ? fromTemplate.kind === 'blocks'
          ? structuredClone(fromTemplate.blockJson)
          : null
        : (b.blockJson ?? null),
      status: 'draft',
      kind: 'campaign',
      scheduledAt: null,
      createdBy: 'u-mikkel',
      approvedBy: null,
      approvedAt: null,
      testSendDoneAt: null,
      createdAt: Date.now(),
      sentAt: null,
      abEnabled: b.abEnabled ?? false,
      abSplitPercent: b.abSplitPercent ?? 15,
      abWaitHours: b.abWaitHours ?? 4,
      subjectB: b.subjectB ?? null,
      htmlB: typeof b.htmlB === 'string' && b.htmlB.trim() ? b.htmlB : null,
      abTestStartedAt: null,
      abWinnerVariant: null,
      abWinnerDecidedAt: null,
      abNoSignificantWinner: false,
      resendOfCampaignId: null,
      resendKind: null,
      coupon: b.coupon ?? null,
    };
    campaignStore.set(campaign.id, campaign);
    audienceStore.set(campaign.id, normalizeAudience(b.audience));
    return json({ campaign: withAudience(campaign) }, 201);
  }

  // Like the API (`duplicateCampaignGroup`): every language of one send
  // copied into new drafts named "<name> (XX)", all or none.
  if (pathname === '/campaigns/duplicate-group' && method === 'POST') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { campaignIds?: string[]; name?: string };
    const ids = b.campaignIds ?? [];
    const name = (b.name ?? '').trim();
    if (ids.length < 2 || !name || name.length > 195) {
      return json({ error: 'invalid_request' }, 400);
    }
    const suffix = /^(.*\S)\s+\(([A-Z]{2})\)$/;
    const originals: { campaign: Campaign; language: string }[] = [];
    for (const id of ids) {
      const c = campaignStore.get(id);
      if (!c || c.kind === 'flow_step') return json({ error: 'not_found' }, 404);
      const m = suffix.exec(c.name.trim());
      if (!m || (originals.length > 0 && m[1] !== suffix.exec(originals[0]!.campaign.name)?.[1])) {
        return json(
          {
            error: 'not_a_group',
            message:
              'These campaigns are not the language versions of one send. Their names must differ only in the language at the end, for example "Halloween (DA)" and "Halloween (DE)".',
          },
          400,
        );
      }
      originals.push({ campaign: c, language: m[2]! });
    }
    if (new Set(originals.map((o) => o.language)).size !== originals.length) {
      return json({ error: 'not_a_group' }, 400);
    }
    const taken = [...campaignStore.values()].find(
      (c) => c.kind !== 'flow_step' && suffix.exec(c.name.trim())?.[1] === name,
    );
    if (taken) {
      return json(
        {
          error: 'name_taken',
          message: `"${taken.name}" already exists. Pick a name no other send uses.`,
        },
        409,
      );
    }
    const copies = originals.map(({ campaign, language }) => {
      const copy: Campaign = {
        ...campaign,
        id: `c-${nextId++}`,
        name: `${name} (${language})`,
        status: 'draft',
        kind: 'campaign',
        scheduledAt: null,
        createdBy: 'u-mikkel',
        approvedBy: null,
        approvedAt: null,
        testSendDoneAt: null,
        createdAt: Date.now(),
        sentAt: null,
        abTestStartedAt: null,
        abWinnerVariant: null,
        abWinnerDecidedAt: null,
        abNoSignificantWinner: false,
        resendOfCampaignId: null,
        resendKind: null,
        archivedAt: null,
        sections: null,
      };
      campaignStore.set(copy.id, copy);
      audienceStore.set(copy.id, normalizeAudience(audienceStore.get(campaign.id)));
      return withAudience(copy);
    });
    return json({ campaigns: copies }, 201);
  }

  // Like the API (apps/app routes/sending.ts): every language of one send
  // in one approval, all or none. Each language is checked like a single
  // approval before the code, so a refused group never spends it.
  if (pathname === '/campaigns/approve-group' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as {
      totpCode?: string;
      recoveryCode?: string;
      items?: {
        campaignId: string;
        snapshotId: string;
        scheduledAt: number | null;
        expectedRecipientCount: number;
      }[];
      expectedTotalRecipientCount?: number;
    };
    const items = b.items ?? [];
    if (items.length < 2 || typeof b.expectedTotalRecipientCount !== 'number') {
      return json({ error: 'invalid_request' }, 400);
    }
    const sum = items.reduce((n, item) => n + item.expectedRecipientCount, 0);
    if (sum !== b.expectedTotalRecipientCount) {
      return json(
        {
          error: 'invalid_request',
          message:
            'The total does not match the recipient counts of the languages. Review them again before you approve.',
        },
        400,
      );
    }
    const bases = new Set<string>();
    const languages = new Set<string>();
    for (const item of items) {
      const c = campaignStore.get(item.campaignId);
      if (!c) return json({ error: 'not_found' }, 404);
      const m = /^(.*\S)\s+\(([A-Z]{2})\)$/.exec(c.name.trim());
      if (!m) bases.add(`\u0000${c.id}`);
      else {
        bases.add(m[1]!);
        languages.add(m[2]!);
      }
    }
    if (bases.size !== 1 || languages.size !== items.length) {
      return json(
        {
          error: 'not_a_group',
          message:
            'These campaigns are not the language versions of one send. Their names must differ only in the language at the end, for example "Halloween (DA)" and "Halloween (DE)".',
        },
        400,
      );
    }
    const failures: { campaignId: string; error: string; message: string }[] = [];
    for (const item of items) {
      const c = campaignStore.get(item.campaignId)!;
      const fail = (error: string, message: string) =>
        failures.push({ campaignId: c.id, error, message });
      if (c.status !== 'draft') {
        fail(
          'campaign_not_approvable',
          c.status === 'scheduled'
            ? 'The campaign has already been approved. It is sent at its scheduled time.'
            : 'Only a draft campaign can be approved.',
        );
      } else if (item.snapshotId !== mockSnapshotId(c.id) || item.scheduledAt !== c.scheduledAt) {
        fail(
          'campaign_changed',
          'The campaign changed since you opened this page. Review it again before you approve.',
        );
      } else if (c.scheduledAt !== null && c.scheduledAt <= Date.now()) {
        fail(
          'schedule_passed',
          'The planned send time has passed. Ask an editor to pick a new time, or to switch to "Right after approval", and review the campaign again.',
        );
      } else if (c.testSendDoneAt === null) {
        fail(
          'test_send_missing',
          'Send a test email of this version first. Then approve the campaign.',
        );
      } else if (item.expectedRecipientCount !== mockRecipientCount(c.id)) {
        fail(
          'recipient_count_changed',
          'The recipient count changed since you opened this page. Review it again before you approve.',
        );
      }
    }
    if (failures.length > 0) {
      return json(
        {
          error: 'group_not_approvable',
          message:
            'Nothing was approved. Fix the languages listed below, then approve them all again.',
          failures,
        },
        409,
      );
    }
    const stepUpError = mockWindowStepUp(b);
    if (stepUpError) return stepUpError;
    for (const item of items) {
      const c = campaignStore.get(item.campaignId)!;
      c.status = 'scheduled';
      c.approvedBy = 'u-mikkel';
      c.approvedAt = Date.now();
    }
    return json({ status: 'success' });
  }

  const campaignMatch = /^\/campaigns\/([^/]+)(\/.*)?$/.exec(pathname);
  if (campaignMatch) {
    const id = campaignMatch[1] as string;
    const sub = campaignMatch[2] ?? '';
    const campaign = campaignStore.get(id);

    // The mock's stand-in for the minute cron: an approved campaign moves
    // from `scheduled` to `sending` once its send time has come.
    if (
      campaign?.status === 'scheduled' &&
      campaign.approvedAt !== null &&
      Date.now() >= Math.max(campaign.scheduledAt ?? 0, campaign.approvedAt + MOCK_CRON_DELAY_MS)
    ) {
      campaign.status = 'sending';
    }

    if (sub === '' && method === 'GET') {
      return campaign
        ? json({ campaign: withAudience(campaign) })
        : json({ error: 'not_found' }, 404);
    }
    if (sub === '' && method === 'PATCH') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      const { audience, audienceEstimate: _ignored, ...patch } = body as Partial<Campaign>;
      // Like the API: blank content B is stored as none.
      if (typeof patch.htmlB === 'string' && !patch.htmlB.trim()) patch.htmlB = null;
      if (campaign.status !== 'draft') {
        return json(
          {
            error: 'invalid_request',
            message: 'The campaign is approved or sent and can no longer be edited.',
          },
          400,
        );
      }
      // Like the API: a send time must be an integer (or null), ahead of now and at most
      // one year out. Only a changed value is checked, so re-saving the stored one is fine.
      if ('scheduledAt' in patch) {
        const at = (patch as { scheduledAt?: unknown }).scheduledAt;
        if (at !== null && !Number.isSafeInteger(at)) {
          return json({ error: 'invalid_request' }, 400);
        }
        if (at !== null && at !== campaign.scheduledAt) {
          if ((at as number) <= Date.now()) {
            return json(
              { error: 'invalid_schedule', message: 'The send time must be in the future.' },
              400,
            );
          }
          if ((at as number) > Date.now() + 365 * 24 * 60 * 60 * 1000) {
            return json(
              {
                error: 'invalid_schedule',
                message: 'The send time can be at most one year ahead.',
              },
              400,
            );
          }
        }
      }
      // Like the API (`updateDraftCampaign`): local-time sending needs a real date, a time
      // of day and, if given, a known fallback zone; the send instant is derived from them.
      const sendPatch = patch as {
        sendMode?: 'fixed' | 'local_time' | 'smart';
        localSendDate?: string | null;
        localSendMinutes?: number | null;
        sendFallbackTz?: string | null;
        scheduledAt?: number | null;
      };
      if (sendPatch.sendMode === 'local_time') {
        const date = sendPatch.localSendDate ?? campaign.localSendDate ?? null;
        const minutes = sendPatch.localSendMinutes ?? campaign.localSendMinutes ?? null;
        const tz =
          sendPatch.sendFallbackTz !== undefined
            ? sendPatch.sendFallbackTz
            : (campaign.sendFallbackTz ?? null);
        let tzOk = true;
        if (tz !== null) {
          try {
            new Intl.DateTimeFormat('en-GB', { timeZone: tz });
          } catch {
            tzOk = false;
          }
        }
        const dateOk =
          date !== null && /^\d{4}-\d{2}-\d{2}$/.test(date) && !isNaN(Date.parse(date));
        if (
          !dateOk ||
          minutes === null ||
          !Number.isInteger(minutes) ||
          minutes < 0 ||
          minutes > 1439 ||
          !tzOk
        ) {
          return json(
            {
              error: 'invalid_local_send',
              message:
                'Local-time sending needs a valid date, a time of day and (optionally) a valid fallback time zone.',
            },
            400,
          );
        }
        // Earliest instant anywhere on earth is UTC+14, so the date must not be older than that.
        const earliest = Date.parse(`${date}T00:00:00Z`) + minutes * 60_000 - 14 * 3_600_000;
        if (earliest + 26 * 3_600_000 <= Date.now()) {
          return json(
            { error: 'invalid_schedule', message: 'The send time must be in the future.' },
            400,
          );
        }
        sendPatch.scheduledAt = earliest;
      } else if (sendPatch.sendMode === 'fixed' || sendPatch.sendMode === 'smart') {
        sendPatch.localSendDate = null;
        sendPatch.localSendMinutes = null;
        sendPatch.sendFallbackTz = null;
      }
      // Like the API: an edit of what is sent (content, subject, sender,
      // recipients) is a new version — the test email and any approval page
      // still showing the old one no longer count.
      const current = campaign as unknown as Record<string, unknown>;
      const mailChanged =
        (
          [
            'name',
            'senderId',
            'subject',
            'subjectB',
            'htmlB',
            'preheader',
            'html',
            'abEnabled',
          ] as const
        ).some((key) => key in patch && (patch as Record<string, unknown>)[key] !== current[key]) ||
        ('coupon' in patch &&
          JSON.stringify(patch.coupon ?? null) !== JSON.stringify(campaign.coupon ?? null)) ||
        (audience !== undefined &&
          JSON.stringify(normalizeAudience(audience)) !== JSON.stringify(audienceStore.get(id)));
      const updated = { ...campaign, ...patch };
      // Like the API: a saved source that differs, or a block model, drops the section edits.
      if (
        ('html' in patch && patch.html !== campaign.html) ||
        ('blockJson' in patch && patch.blockJson !== null)
      ) {
        updated.sections = null;
      }
      if (mailChanged) {
        updated.testSendDoneAt = null;
        snapshotVersion.set(id, (snapshotVersion.get(id) ?? 1) + 1);
      }
      campaignStore.set(id, updated);
      if (audience) audienceStore.set(id, normalizeAudience(audience));
      return json({ campaign: withAudience(updated) });
    }
    if (sub === '/sections' && method === 'PUT') {
      if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
      if (!campaign) return json({ error: 'not_found' }, 404);
      if (campaign.blockJson !== null) {
        return json(
          {
            error: 'sections_not_html',
            message: 'Sections are for HTML campaigns. This campaign is edited with blocks.',
          },
          409,
        );
      }
      if (campaign.status !== 'draft') {
        return json(
          {
            error: 'invalid_request',
            message: 'The campaign has been approved or sent and can no longer be edited.',
          },
          400,
        );
      }
      const plan = mockSectionSave(body, mockSectionOriginal(campaign));
      if (!plan.ok) return json(plan.body, plan.status);
      const updated: Campaign = { ...campaign, html: plan.html, sections: plan.sections };
      if (plan.html !== campaign.html) {
        updated.testSendDoneAt = null;
        snapshotVersion.set(id, (snapshotVersion.get(id) ?? 1) + 1);
      }
      campaignStore.set(id, updated);
      return json({ campaign: withAudience(updated), unmapped: plan.unmapped });
    }
    // Mirrors `duplicateCampaign`: content, subject(s), preview text, sender
    // and audience are copied; status, approval, schedule and A/B results are not.
    if (sub === '/duplicate' && method === 'POST') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      const copy: Campaign = {
        ...campaign,
        id: `c-${nextId++}`,
        name: `${campaign.name.slice(0, 193)} (copy)`,
        status: 'draft',
        kind: 'campaign',
        scheduledAt: null,
        createdBy: 'u-mikkel',
        approvedBy: null,
        approvedAt: null,
        testSendDoneAt: null,
        createdAt: Date.now(),
        sentAt: null,
        abTestStartedAt: null,
        abWinnerVariant: null,
        abWinnerDecidedAt: null,
        abNoSignificantWinner: false,
        resendOfCampaignId: null,
        resendKind: null,
        archivedAt: null,
        // K6, like the API: the send mode, local time and A/B auto-send are kept ('smart'
        // becomes 'fixed'); a local date already in the past is dropped, never the schedule.
        sendMode: campaign.sendMode === 'local_time' ? 'local_time' : 'fixed',
        localSendDate:
          campaign.sendMode === 'local_time' &&
          campaign.localSendDate &&
          campaign.localSendDate >= new Date().toISOString().slice(0, 10)
            ? campaign.localSendDate
            : null,
        localSendMinutes:
          campaign.sendMode === 'local_time' ? (campaign.localSendMinutes ?? null) : null,
        sendFallbackTz:
          campaign.sendMode === 'local_time' ? (campaign.sendFallbackTz ?? null) : null,
        // Like the API: a copy starts from its HTML, without the section edits behind it.
        sections: null,
      };
      campaignStore.set(copy.id, copy);
      audienceStore.set(copy.id, normalizeAudience(audienceStore.get(id)));
      return json({ campaign: withAudience(copy) }, 201);
    }
    // Mirrors `setCampaignArchived`: only a sent or failed campaign can be
    // archived (the first time is kept); bringing one back always works.
    if (sub === '/archive' && method === 'POST') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      const archived = (body as { archived?: unknown })?.archived;
      if (typeof archived !== 'boolean') return json({ error: 'invalid_request' }, 400);
      if (archived && campaign.status !== 'sent' && campaign.status !== 'failed') {
        return json(
          { error: 'invalid_request', message: 'Only a sent or failed campaign can be archived.' },
          400,
        );
      }
      const updated: Campaign = {
        ...campaign,
        archivedAt: archived ? (campaign.archivedAt ?? Date.now()) : null,
      };
      campaignStore.set(id, updated);
      return json({ campaign: withAudience(updated) });
    }
    if (sub === '' && method === 'DELETE') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      if (campaign.status !== 'draft') {
        return json(
          { error: 'invalid_request', message: 'Only a draft campaign may be deleted.' },
          400,
        );
      }
      campaignStore.delete(id);
      return json({ status: 'success' });
    }
    if (sub === '/preview' && method === 'POST') {
      const html = (body as { html?: string })?.html ?? campaign?.html ?? '';
      return json({
        valid: true,
        errors: [],
        sanitizedHtml: html,
        sizeBytes: new TextEncoder().encode(html).length,
      });
    }
    if (sub === '/preflight' && method === 'POST') {
      const html = campaign?.html ?? '<p></p>';
      return json({
        ok: true,
        sizeBytes: new TextEncoder().encode(html).length,
        checks: [
          { id: 'links', status: 'ok', title: 'Links', message: 'All links are valid.' },
          {
            id: 'alt-text',
            status: 'ok',
            title: 'Alt text',
            message: 'All images have alt text.',
          },
          {
            id: 'gmail-clip',
            status: 'ok',
            title: 'Gmail clipping',
            message: `HTML is ${(new TextEncoder().encode(html).length / 1024).toFixed(1)} KB (limit 102 KB).`,
          },
          {
            id: 'unsubscribe',
            status: 'ok',
            title: 'Unsubscribe link',
            message: 'Footer contains an unsubscribe link.',
          },
          {
            id: 'preferences-link',
            status: 'ok',
            title: 'Preferences link',
            message: 'Preferences link found.',
          },
          {
            id: 'subject',
            status: campaign?.subject ? 'ok' : 'warn',
            title: 'Subject line',
            message: campaign?.subject ? 'Subject line filled in.' : 'Subject line missing.',
          },
        ],
        previews: {
          html: `<div style="font-family:sans-serif;padding:16px">${html}</div>`,
          text: mockPlainText(html),
        },
      });
    }
    if (sub === '/test-send' && method === 'POST') {
      // Like the API: only users of this tenant (the mock has one).
      const toEmail = (body as { toEmail?: string } | undefined)?.toEmail;
      if (toEmail?.trim().toLowerCase() !== 'mikkel@example.com') {
        return json(
          {
            error: 'test_recipient_not_member',
            message: 'Test emails can only be sent to users with access to this account.',
          },
          403,
        );
      }
      // Only a draft's test email is recorded (it is the prepared version).
      if (campaign?.status === 'draft') campaign.testSendDoneAt = Date.now();
      return json({ status: 'success' });
    }
    // Prepare/approve/pause/resume follow the API's state machine
    // (apps/app routes/sending.ts): draft -> approve -> scheduled -> (cron)
    // sending; pause -> paused -> resume -> scheduled. Same 404/409s.
    if (sub === '/prepare' && method === 'POST') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      if (campaign.status !== 'draft') {
        return json(
          {
            error: 'campaign_not_preparable',
            message:
              'The campaign has been approved or sent and can no longer be prepared. Content and recipients are locked. To change it, pause the campaign and send a copy instead.',
          },
          409,
        );
      }
      const recipientCount = mockRecipientCount(id);
      return json({ recipientCount, messageCount: recipientCount, snapshotId: mockSnapshotId(id) });
    }
    if ((sub === '/approve' || sub === '/resume') && method === 'POST') {
      const b = body as {
        totpCode?: string;
        recoveryCode?: string;
        snapshotId?: string;
        scheduledAt?: number | null;
        expectedRecipientCount?: number;
      };
      if (sub === '/approve' && !b.snapshotId) return json({ error: 'invalid_request' }, 400);
      // The approval names the send time the admin saw (the key is required, null = right away).
      if (sub === '/approve' && b.scheduledAt === undefined) {
        return json({ error: 'invalid_request' }, 400);
      }
      if (!campaign) return json({ error: 'not_found' }, 404);
      const required = sub === '/approve' ? 'draft' : 'paused';
      if (campaign.status !== required) {
        return sub === '/approve'
          ? json(
              {
                error: 'campaign_not_approvable',
                message:
                  campaign.status === 'scheduled'
                    ? 'The campaign has already been approved. It is sent at its scheduled time.'
                    : campaign.status === 'paused'
                      ? 'The campaign is paused. Resume it to approve the send again.'
                      : 'Only a draft campaign can be approved.',
              },
              409,
            )
          : json(
              { error: 'campaign_not_paused', message: 'Only a paused campaign can be resumed.' },
              409,
            );
      }
      // A resume names the count the admin saw, except a flow step (its count moves on its own).
      if (
        sub === '/resume' &&
        campaign.kind !== 'flow_step' &&
        typeof b.expectedRecipientCount !== 'number'
      ) {
        return json({ error: 'invalid_request' }, 400);
      }
      // Checked before the code, like the API: the version the admin
      // reviewed must still be the prepared one.
      if (sub === '/approve' && b.snapshotId !== mockSnapshotId(id)) {
        return json(
          {
            error: 'campaign_changed',
            message:
              'The campaign changed since you opened this page. Review it again before you approve.',
          },
          409,
        );
      }
      if (sub === '/approve' && b.scheduledAt !== campaign.scheduledAt) {
        return json(
          {
            error: 'campaign_changed',
            message:
              'The campaign changed since you opened this page. Review it again before you approve.',
          },
          409,
        );
      }
      // A planned time that has passed is refused, never turned into "send now".
      if (
        sub === '/approve' &&
        campaign.scheduledAt !== null &&
        campaign.scheduledAt <= Date.now()
      ) {
        return json(
          {
            error: 'schedule_passed',
            message:
              'The planned send time has passed. Ask an editor to pick a new time, or to switch to "Right after approval", and review the campaign again.',
          },
          409,
        );
      }
      // Also before the code, like the API: a draft with no test email.
      if (sub === '/approve' && campaign.testSendDoneAt === null) {
        return json(
          {
            error: 'test_send_missing',
            message: 'Send a test email of this version first. Then approve the campaign.',
          },
          409,
        );
      }
      const stepUpError = mockWindowStepUp(b);
      if (stepUpError) return stepUpError;
      // After the code, like the API: a paused sender would leave the resumed campaign sending nothing.
      if (sub === '/resume' && senderPaused.paused) {
        return json(
          {
            error: 'sender_paused',
            message: `The sender is paused${senderPaused.reason ? `: ${senderPaused.reason}` : '.'} Resume the sender first (Senders).`,
            senderId: senderPaused.senderId,
            pauseKind: senderPaused.kind,
          },
          409,
        );
      }
      campaign.status = 'scheduled';
      campaign.approvedBy = 'u-mikkel';
      campaign.approvedAt = Date.now();
      return json({ status: 'success' });
    }
    if (sub === '/pause' && method === 'POST') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      if (campaign.status === 'paused') return json({ status: 'success' });
      if (campaign.status !== 'scheduled' && campaign.status !== 'sending') {
        return json(
          {
            error: 'campaign_not_pausable',
            message: 'Only a campaign that is waiting to be sent or sending can be paused.',
          },
          409,
        );
      }
      campaign.status = 'paused';
      campaign.approvedBy = null;
      campaign.approvedAt = null;
      return json({ status: 'success' });
    }
    if (sub === '/counts' && method === 'GET') {
      // `toSend` is what an approval or a resume covers: queued + retrying (the mock has no held-back messages).
      return json({ counts: { ...mockCounts, toSend: mockCounts.queued + mockCounts.retryable } });
    }
    if (sub === '/messages/unknown' && method === 'GET') {
      return json({ messages: [] });
    }
    if (sub === '/messages/dead-letter' && method === 'GET') {
      return json({ messages: [] });
    }
    if (sub === '/ab-winner' && method === 'POST') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      const variant = (body as { variant?: unknown } | undefined)?.variant;
      if (variant !== 'a' && variant !== 'b') return json({ error: 'invalid_request' }, 400);
      if (!abAwaitingPick || mockAbPicked) {
        return json(
          { error: 'ab_not_waiting', message: 'This test is not waiting for a winner.' },
          409,
        );
      }
      mockAbPicked = variant;
      return json({ ok: true, winner: variant });
    }
    if (sub === '/resend-non-clickers' && method === 'POST') {
      if (!campaign) return json({ error: 'not_found' }, 404);
      if (campaign.status !== 'sent') {
        return json(
          {
            error: 'invalid_request',
            message: 'Only a sent campaign can be resent to non-clickers.',
          },
          400,
        );
      }
      const RESEND_MIN_DELAY_MS = 24 * 60 * 60 * 1000;
      if (Date.now() - (campaign.sentAt ?? 0) < RESEND_MIN_DELAY_MS) {
        return json(
          {
            error: 'invalid_request',
            message: 'A resend can be made no earlier than 24 hours after the original send.',
          },
          400,
        );
      }
      const alreadyResent = Array.from(campaignStore.values()).some(
        (c) => c.resendOfCampaignId === campaign.id,
      );
      if (alreadyResent) {
        return json(
          {
            error: 'invalid_request',
            message: 'This campaign has already been resent to non-clickers.',
          },
          400,
        );
      }
      const subject = (body as { subject?: string } | undefined)?.subject;
      const resend: Campaign = {
        id: `c-${nextId++}`,
        senderId: campaign.senderId,
        templateId: campaign.templateId,
        name: `${campaign.name} (resend to non-clickers)`,
        subject: subject ?? campaign.subject,
        preheader: null,
        html: campaign.html,
        blockJson: campaign.blockJson,
        status: 'draft',
        kind: 'campaign',
        scheduledAt: null,
        createdBy: 'u-mikkel',
        approvedBy: null,
        approvedAt: null,
        testSendDoneAt: null,
        createdAt: Date.now(),
        sentAt: null,
        abEnabled: false,
        abSplitPercent: 15,
        abWaitHours: 4,
        subjectB: null,
        // Like the API: a resend is never an A/B test, so content B is not copied.
        htmlB: null,
        abTestStartedAt: null,
        abWinnerVariant: null,
        abWinnerDecidedAt: null,
        abNoSignificantWinner: false,
        resendOfCampaignId: campaign.id,
        resendKind: 'non_clickers',
        // Like the API: the resend keeps the personal discount code setting.
        coupon: campaign.coupon ?? null,
      };
      campaignStore.set(resend.id, resend);
      audienceStore.set(resend.id, normalizeAudience(undefined));
      return json({ campaign: { id: resend.id } }, 201);
    }
  }

  // --- stats ---
  if (pathname === '/stats/campaigns' && method === 'GET') {
    if (abAwaitingPick) {
      return json({
        items: mockStatsList.map((i) => (i.id === 'c-sep' ? { ...i, status: 'sending' } : i)),
      });
    }
    return json({ items: mockStatsList });
  }
  // Like the API: default last 30 days, at most 92, under 200 sent = "insufficient".
  if (pathname === '/stats/deliverability' && method === 'GET') {
    const to = Number(params.get('to')) || Date.now();
    const from = Number(params.get('from')) || to - 30 * 24 * 3600 * 1000;
    if (from >= to || to - from > 92 * 24 * 3600 * 1000) {
      return json({ error: 'invalid_range' }, 400);
    }
    const inRange = mockStatsList.filter(
      (s) => s.sentAt !== null && s.sentAt >= from && s.sentAt < to,
    );
    const total = (key: 'sent' | 'bounced' | 'complained' | 'unsubscribed') =>
      inRange.reduce((n, s) => n + s[key], 0);
    const sent = total('sent');
    const rate = (n: number) => (sent > 0 ? n / sent : 0);
    const grade = (r: number, amber: number, red: number) =>
      sent < 200 ? 'insufficient' : r >= red ? 'red' : r >= amber ? 'amber' : 'green';
    const bounceRate = rate(total('bounced'));
    const complaintRate = rate(total('complained'));
    const unsubscribeRate = rate(total('unsubscribed'));
    const bounce = grade(bounceRate, 0.02, 0.05);
    const complaint = grade(complaintRate, 0.001, 0.003);
    const unsubscribe = grade(unsubscribeRate, 0.005, 0.01);
    const order = ['green', 'amber', 'red'];
    const overall =
      sent < 200
        ? 'insufficient'
        : [bounce, complaint, unsubscribe].reduce((w, x) =>
            order.indexOf(x) > order.indexOf(w) ? x : w,
          );
    return json({
      sent,
      bounceRate,
      complaintRate,
      unsubscribeRate,
      bounce,
      complaint,
      unsubscribe,
      overall,
    });
  }
  if (pathname === '/stats/overview' && method === 'GET') {
    const from = Number(params.get('from'));
    const to = Number(params.get('to'));
    if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
      return json({ error: 'invalid_range' }, 400);
    }
    const inRange = mockStatsList.filter(
      (s) => s.sentAt !== null && s.sentAt >= from && s.sentAt < to,
    );
    const sum = (key: keyof (typeof mockStatsList)[number]) =>
      inRange.reduce((total, s) => total + (s[key] as number), 0);
    // About 6% bot clicks on top, as measured on NoPain's own mails (Oct 2026).
    const botClicked = Math.round(sum('clicked') * 0.06);
    return json({
      sent: sum('sent'),
      delivered: sum('delivered'),
      clicked: sum('clicked') + (params.get('bots') === 'include' ? botClicked : 0),
      botClicked,
      unsubscribed: sum('unsubscribed'),
      bounced: sum('bounced'),
      complained: sum('complained'),
    });
  }
  if (pathname === '/stats/sending-roi' && method === 'GET') {
    const from = Number(params.get('from') || NaN);
    const to = Number(params.get('to') || NaN);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
      return json({ error: 'invalid_range' }, 400);
    }
    const inRange = mockStatsList.filter(
      (s) => s.sentAt !== null && s.sentAt >= from && s.sentAt < to,
    );
    const sent = inRange.reduce((t, s) => t + s.sent, 0);
    const orders = inRange.reduce((t, s) => t + mockCampaignRevenue(s.id).orders, 0);
    const revenue = inRange.reduce((t, s) => t + mockCampaignRevenue(s.id).revenue, 0);
    const onHold = {
      orders: inRange.reduce((t, s) => t + mockCampaignRevenue(s.id).onHoldOrders, 0),
      revenue: inRange.reduce((t, s) => t + mockCampaignRevenue(s.id).onHoldRevenue, 0),
    };
    return json(mockSendingRoi(sent, orders, revenue, sendCostSetting.costPerThousand, onHold));
  }
  const statsMatch = /^\/stats\/campaigns\/([^/]+)$/.exec(pathname);
  if (statsMatch && method === 'GET') {
    const stored = mockStatsDetail[statsMatch[1] as string];
    if (!stored) return json({ error: 'not_found' }, 404);
    const detail =
      abAwaitingPick && stored.id === 'c-sep' && stored.ab
        ? {
            ...stored,
            ab: {
              ...stored.ab,
              autoSend: false,
              winnerVariant: mockAbPicked,
              winnerDecidedAt: null,
            },
          }
        : stored;
    // Re-priced with the current setting, like the server.
    const { sent, orders, revenue, onHoldOrders, onHoldRevenue } = detail.roi;
    return json({
      ...detail,
      roi: mockSendingRoi(sent, orders, revenue, sendCostSetting.costPerThousand, {
        orders: onHoldOrders,
        revenue: onHoldRevenue,
      }),
    });
  }
  if (pathname === '/buyers/overview' && method === 'GET') {
    const period = Number(params.get('period') ?? 365);
    if (period !== 365 && period !== 90 && period !== 30)
      return json({ error: 'invalid_query' }, 400);
    return json(
      mockBuyersOverview(
        period,
        (params.get('country') ?? 'all').toUpperCase() === 'ALL'
          ? 'all'
          : (params.get('country') ?? 'all').toUpperCase(),
      ),
    );
  }
  if (pathname === '/buyers/why' && method === 'GET') {
    const why = mockBuyersWhy(params.get('campaign') ?? 'all');
    return why ? json(why) : json({ error: 'not_found' }, 404);
  }
  if (pathname === '/buyers/groups' && method === 'GET') {
    return json(mockBuyersGroups());
  }
  if (pathname === '/stats/dashboard' && method === 'GET') {
    // Fake numbers for the mock mode only.
    const items = [
      {
        kind: 'campaign',
        key: 'mock-c1',
        name: 'Autumn newsletter',
        sent: 1000,
        delivered: 980,
        opened: 400,
        clicked: 90,
        unsubscribed: 3,
        bounced: 12,
        complained: 1,
        orders: 12,
        revenue: 640,
      },
      {
        kind: 'flow',
        key: 'mock-a1',
        name: 'Welcome series',
        sent: 200,
        delivered: 198,
        opened: 120,
        clicked: 40,
        unsubscribed: 1,
        bounced: 2,
        complained: 0,
        orders: 5,
        revenue: 210,
      },
      {
        kind: 'campaign',
        key: 'mock-c2',
        name: 'Winter preview',
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        unsubscribed: 0,
        bounced: 0,
        complained: 0,
        orders: 0,
        revenue: 0,
      },
    ];
    const totals = items.reduce(
      (t, i) => ({
        sent: t.sent + i.sent,
        delivered: t.delivered + i.delivered,
        opened: t.opened + i.opened,
        clicked: t.clicked + i.clicked,
        unsubscribed: t.unsubscribed + i.unsubscribed,
        bounced: t.bounced + i.bounced,
        complained: t.complained + i.complained,
        orders: t.orders + i.orders,
        revenue: t.revenue + i.revenue,
      }),
      {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        unsubscribed: 0,
        bounced: 0,
        complained: 0,
        orders: 0,
        revenue: 0,
      },
    );
    const day = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    return json({
      from: params.get('from') ?? day,
      to: params.get('to') ?? day,
      currency: 'EUR',
      daysRolled: 29,
      daysInRange: 30,
      rolledThrough: day,
      lastRolledAt: Date.now() - 6 * 60 * 60 * 1000,
      daysNotFinal: 7,
      daysCatchingUp: 1,
      items,
      daily: mockDashboardDaily(params.get('from') ?? day, params.get('to') ?? day),
      totals,
    });
  }
  if (pathname === '/stats/dashboard/rollup' && method === 'POST') {
    return json({ days: [], queries: 2, moreDue: false });
  }
  if (pathname === '/stats/sources' && method === 'GET') {
    const to = Number(params.get('to') ?? Date.now());
    const from = Number(params.get('from') ?? to - 30 * 24 * 60 * 60 * 1000);
    return json({ items: mockSourceStats, period: { from, to } });
  }

  // --- suppressions (Audience → Unsubscribed) ---
  if (pathname === '/suppressions' && method === 'GET') {
    return json({
      counts: { unsubscribed: 3160, hard_bounce: 851, complaint: 0, manual: 0 },
      unsubscribesBySource: { header_one_click: 0, link_click: 2, manual: 0, import: 3158 },
      reasons: {
        byReason: { too_many: 1, not_relevant: 0, not_customer: 0, by_mistake: 0, other: 0 },
        total: mockUnsubscribes().length,
      },
    });
  }
  if (pathname === '/suppressions/unsubscribes' && method === 'GET') {
    const reason = params.get('reason');
    const q = (params.get('q') ?? '').trim().toLowerCase();
    const items = mockUnsubscribes()
      .filter((u) =>
        reason === null ? true : reason === 'none' ? u.reason === null : u.reason === reason,
      )
      .filter((u) => !q || (u.email?.includes(q) ?? false));
    return json({ items, total: items.length, nextCursor: null });
  }
  if (pathname === '/suppressions/bounces/summary' && method === 'GET') {
    const campaign = params.get('campaign');
    const rows = mockBounces().filter((b) => !campaign || b.campaign?.id === campaign);
    const reasons = {
      typo: 0,
      corrected: 0,
      nodomain: 0,
      nobox: 0,
      suppressed: 0,
      full: 0,
      refused: 0,
      spam: 0,
    };
    for (const b of rows) reasons[b.reason] += 1;
    const campaigns = new Map<string, string>();
    for (const b of mockBounces()) if (b.campaign) campaigns.set(b.campaign.id, b.campaign.name);
    return json({
      total: rows.length,
      reasons,
      campaigns: [...campaigns].map(([id, name]) => ({ id, name })),
    });
  }
  if (pathname === '/suppressions/bounces' && method === 'GET') {
    const q = (params.get('q') ?? '').trim().toLowerCase();
    const reason = params.get('reason');
    const campaign = params.get('campaign');
    const limit = Number(params.get('limit') ?? 50);
    const start = Number(params.get('cursor') ?? 0);
    const matching = mockBounces().filter(
      (b) =>
        (!q || b.email.includes(q)) &&
        (!reason || b.reason === reason) &&
        (!campaign || b.campaign?.id === campaign),
    );
    const items = matching.slice(start, start + limit);
    const next = start + limit;
    return json({
      items,
      total: matching.length,
      nextCursor: next < matching.length ? String(next) : null,
    });
  }
  const bounceContactMatch = /^\/suppressions\/bounces\/([^/]+)\/contact$/.exec(pathname);
  if (bounceContactMatch && method === 'GET') {
    const found = mockBounces().find((b) => b.id === bounceContactMatch[1]);
    if (!found) return json({ error: 'not_found' }, 404);
    return json(mockBounceContact(found));
  }
  const fixAddressMatch = /^\/suppressions\/bounces\/([^/]+)\/fix-address$/.exec(pathname);
  if (fixAddressMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const stepUpFailed = mockWindowStepUp(
      (body ?? {}) as { totpCode?: string; recoveryCode?: string },
    );
    if (stepUpFailed) return stepUpFailed;
    const found = mockBounces().find((b) => b.id === fixAddressMatch[1]);
    if (!found) return json({ error: 'not_found' }, 404);
    const overview = mockBounceContact(found);
    if (!overview.corrected) return json({ error: 'not_a_typo', contactId: null }, 409);
    if (!overview.contact) return json({ error: 'contact_missing', contactId: null }, 409);
    if (overview.corrected.contactId) {
      return json({ error: 'address_taken', contactId: overview.corrected.contactId }, 409);
    }
    fixedBounceIds.add(found.id);
    const sendLatest = (body as { sendLatest?: boolean } | undefined)?.sendLatest === true;
    return json({
      contactId: overview.contact.id,
      ordersLinked: overview.corrected.orders.count,
      resend: sendLatest ? mockResend(found) : null,
    });
  }
  const resendMatch = /^\/suppressions\/bounces\/([^/]+)\/resend$/.exec(pathname);
  if (resendMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const stepUpFailed = mockWindowStepUp(
      (body ?? {}) as { totpCode?: string; recoveryCode?: string },
    );
    if (stepUpFailed) return stepUpFailed;
    const found = mockBounces().find((b) => b.id === resendMatch[1]);
    if (!found || !mockBounceContact(found).resend) return json({ error: 'not_found' }, 404);
    return json({ resend: mockResend(found) });
  }
  const removeEmailMatch = /^\/suppressions\/unsubscribes\/([^/]+)\/email$/.exec(pathname);
  if (removeEmailMatch && method === 'DELETE') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const found = mockUnsubscribes().find((u) => u.id === removeEmailMatch[1] && u.email);
    return found ? json({ removed: 1 }) : json({ error: 'not_found' }, 404);
  }
  const unsubscribeMatch = /^\/suppressions\/unsubscribes\/([^/]+)$/.exec(pathname);
  if (unsubscribeMatch && method === 'GET') {
    const found = mockUnsubscribes().find((u) => u.id === unsubscribeMatch[1]);
    return found ? json(found) : json({ error: 'not_found' }, 404);
  }
  // Admin only, like the real route. Any address at gone.example is "unsubscribed".
  if (pathname === '/suppressions/lookup' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const email = String((body as { email?: unknown } | undefined)?.email ?? '')
      .trim()
      .toLowerCase();
    if (email.endsWith('@gone.example')) {
      const at = Date.now() - 2 * 60 * 60 * 1000;
      return json({
        suppression: { reason: 'unsubscribed', since: at },
        lastUnsubscribe: { at, source: 'link_click' },
        contactStatus: null,
      });
    }
    const contact = mockContacts.find((c) => c.email.toLowerCase() === email);
    return json({
      suppression: null,
      lastUnsubscribe: null,
      contactStatus: contact?.status ?? null,
    });
  }

  // --- contacts ---
  // Mirrors buildContactsFilter: substring on email or "first last".
  const mockContactMatches = (c: (typeof mockContacts)[number], q: string): boolean => {
    const needle = q.trim().toLowerCase();
    const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.toLowerCase();
    return c.email.toLowerCase().includes(needle) || name.includes(needle);
  };
  if (pathname === '/contacts' && method === 'GET') {
    const q = (params.get('q') ?? '').toLowerCase();
    const status = params.get('status');
    const limit = Number(params.get('limit') ?? '50');
    // Opaque cursor like the server's keyset cursor: callers must only echo `nextCursor`.
    const cursorParam = params.get('cursor');
    let offset = 0;
    if (cursorParam) {
      const m = /^mock:(\d+)$/.exec(atob(cursorParam));
      if (!m) return json({ error: 'invalid_request', message: 'cursor is invalid.' }, 400);
      offset = Number(m[1]);
    }
    if (status !== null && status !== 'active' && status !== 'suppressed') {
      return json(
        { error: 'invalid_request', message: 'status must be active or suppressed.' },
        400,
      );
    }
    const label = params.get('label');
    const language = params.get('language');
    const country = params.get('country');
    let items = mockContacts;
    if (q) items = items.filter((c) => mockContactMatches(c, q));
    if (status) items = items.filter((c) => c.status === status);
    if (label) items = items.filter((c) => labelsFor(c.id).some((l) => l.label === label));
    if (language) items = items.filter((c) => c.language === language);
    if (country) items = items.filter((c) => c.country === country);
    // The 42-row sample array only exists to populate the list/table UI.
    // The one real number this app is allowed to show is 25.144 active
    // contacts, so an unfiltered (or active-only) search with no text
    // query and no label filter reports that as `total` instead of the
    // sample array's length.
    const total =
      !q && !label && !language && !country && (!status || status === 'active')
        ? 25144
        : items.length;
    const page = items.slice(offset, offset + limit);
    const nextOffset = offset + limit;
    return json({
      items: page,
      nextCursor: nextOffset < items.length ? btoa(`mock:${nextOffset}`) : null,
      total,
    });
  }
  // Admin only, like the real route. The sample file holds the 42 sample
  // contacts only (the 25.144 shown on the list is a real figure, but its
  // rows do not exist in the mock), with the real file's first columns.
  if (pathname === '/contacts/export' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const stepUpError = mockStepUpError(body);
    if (stepUpError) return stepUpError;
    const filters = (body ?? {}) as ExportFilters;
    let items = mockContacts;
    if (filters.q) items = items.filter((c) => mockContactMatches(c, filters.q ?? ''));
    if (filters.status) items = items.filter((c) => c.status === filters.status);
    if (filters.label) {
      items = items.filter((c) => labelsFor(c.id).some((l) => l.label === filters.label));
    }
    const lines = [
      'email,status,consent_category,created_at',
      ...items.map(
        (c) =>
          `${c.email},${c.status},${c.consentCategory ?? ''},${new Date(c.createdAt).toISOString()}`,
      ),
    ];
    return new Response(`\uFEFF${lines.join('\r\n')}\r\n`, {
      status: 200,
      headers: {
        'content-type': 'text/csv; charset=utf-8',
        'content-disposition': 'attachment; filename="contacts-sample.csv"',
      },
    });
  }
  const contactExportMatch = /^\/contacts\/([^/]+)\/export$/.exec(pathname);
  if (contactExportMatch && method === 'GET') {
    const id = contactExportMatch[1] as string;
    const contact = mockContactDetail(id);
    const payload = JSON.stringify(
      { contact, consent: mockConsentHistory(id), history: mockContactHistory(id) },
      null,
      2,
    );
    return new Response(payload, {
      status: 200,
      headers: {
        'content-type': 'application/json',
        'content-disposition': `attachment; filename="contact-${id}.json"`,
      },
    });
  }
  const contactMatch = /^\/contacts\/([^/]+)$/.exec(pathname);
  if (contactMatch && method === 'GET') {
    const id = contactMatch[1] as string;
    const base = mockContactDetail(id);
    if (!base) return json({ error: 'not_found' }, 404);
    const edit = mockContactEdit(id);
    const contact = edit
      ? {
          ...base,
          ...(edit.firstName !== undefined ? { firstName: edit.firstName } : {}),
          ...(edit.lastName !== undefined ? { lastName: edit.lastName } : {}),
          ...(edit.language !== undefined ? { language: edit.language } : {}),
          ...(edit.country !== undefined ? { country: edit.country } : {}),
        }
      : base;
    return json({
      contact,
      fields: mockContactFields(id),
      consent: mockConsentHistory(id),
      history: mockContactHistory(id),
      labels: labelsFor(id),
      predictions: mockContactPrediction(id),
    });
  }
  const contactLabelAdd = /^\/contacts\/([^/]+)\/labels$/.exec(pathname);
  if (contactLabelAdd && method === 'POST') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const id = contactLabelAdd[1] as string;
    const label = (body as { label?: unknown } | undefined)?.label;
    if (typeof label !== 'string' || !/^[a-z0-9æøå-]{1,40}$/.test(label)) {
      return json(
        { error: 'invalid_label', message: 'label must be 1-40 characters of a-z, 0-9, æøå or -.' },
        400,
      );
    }
    const current = labelsFor(id);
    if (!current.some((l) => l.label === label)) {
      contactLabelEdits.set(id, [...current, { label, source: 'manual' }]);
    }
    return json({ ok: true }, 201);
  }
  const contactLabelRemove = /^\/contacts\/([^/]+)\/labels\/([^/]+)$/.exec(pathname);
  if (contactLabelRemove && method === 'DELETE') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const id = contactLabelRemove[1] as string;
    const label = decodeURIComponent(contactLabelRemove[2] as string);
    const current = labelsFor(id);
    contactLabelEdits.set(
      id,
      current.filter((l) => !(l.label === label && l.source === 'manual')),
    );
    return json({ ok: true });
  }
  if (contactMatch && method === 'DELETE') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const stepUpError = mockStepUpError(body);
    if (stepUpError) return stepUpError;
    return json({ deleted: true });
  }

  // --- media (billedupload) ---
  const rehostResponse = handleMockRehost(method, pathname, body, mockRole);
  if (rehostResponse) return rehostResponse;
  const mediaResponse = await handleMockMedia(method, pathname, params, init, mockRole);
  if (mediaResponse) return mediaResponse;
  const wooResponse = handleMockWoo(method, pathname, params, body, mockRole);
  if (wooResponse) return wooResponse;
  const formsResponse = handleMockForms(method, pathname, body, mockRole, popupSettings.enabled);
  if (formsResponse) return formsResponse;
  const landingResponse = handleMockLandingPages(method, pathname, body, mockRole);
  if (landingResponse) return landingResponse;
  const aiResponse = handleMockAi(method, pathname, body, mockRole);
  if (aiResponse) return aiResponse;

  // --- brand-kit ---
  if (pathname === '/brand-kit' && method === 'GET') {
    return json(mockBrandKit);
  }
  if (pathname === '/brand-kit' && method === 'PUT') {
    Object.assign(mockBrandKit, body, { updatedAt: Date.now() });
    return json(mockBrandKit);
  }

  // --- reviews (anmeldelsesflow, migration 0025) ---
  // Sikkerhedsreview 28/9, finding 10: `feedback` is personal data, so the
  // list/detail routes are editor+ here too, matching
  // `packages/core/src/db/tenant/reviews.repo.ts`'s `requireRole(ctx,
  // 'editor')` (stats stays viewer+ — no contact data).
  if (pathname === '/reviews' && method === 'GET') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const status = params.get('status');
    if (status !== null && status !== 'open' && status !== 'resolved') {
      return json({ error: 'invalid_request' }, 400);
    }
    const items = status ? mockReviewItems.filter((it) => it.status === status) : mockReviewItems;
    return json({ items: [...items].sort((a, b) => b.createdAt - a.createdAt) });
  }
  if (pathname === '/reviews/stats' && method === 'GET') {
    return json(mockReviewStats);
  }
  const reviewItemMatch = /^\/reviews\/([^/]+)$/.exec(pathname);
  if (reviewItemMatch && method === 'GET') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const item = mockReviewItems.find((it) => it.id === reviewItemMatch[1]);
    if (!item) return json({ error: 'not_found' }, 404);
    return json(item);
  }
  const reviewResolveMatch = /^\/reviews\/([^/]+)\/resolve$/.exec(pathname);
  if (reviewResolveMatch && method === 'POST') {
    if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
    const item = mockReviewItems.find((it) => it.id === reviewResolveMatch[1]);
    if (!item) return json({ error: 'not_found' }, 404);
    item.status = 'resolved';
    item.resolvedAt = Date.now();
    item.resolvedBy = 'mikkel@nopain.example';
    item.updatedAt = item.resolvedAt;
    return json(item);
  }

  // --- samtykketekster (migration 0009) ---
  if (pathname === '/consent-texts' && method === 'GET') {
    return json({ items: [...consentTextStore].reverse() });
  }
  if (pathname === '/consent-texts' && method === 'POST') {
    const text = String((body as { text?: unknown } | undefined)?.text ?? '').trim();
    if (!text)
      return json({ error: 'invalid_request', message: 'The text must not be empty.' }, 400);
    const kind =
      (body as { kind?: unknown } | undefined)?.kind === 'checkout' ? 'checkout' : 'form';
    const rawLanguage = (body as { language?: unknown } | undefined)?.language;
    const language = typeof rawLanguage === 'string' ? rawLanguage.slice(0, 2).toLowerCase() : null;
    const created: ConsentText = {
      version: `v${consentTextStore.length + 1}`,
      text,
      kind,
      language,
      createdAt: Date.now(),
      firstUsedAt: null,
      locked: false,
    };
    consentTextStore.push(created);
    return json(created, 201);
  }

  // --- kundemærkater (fase 5) ---
  if (pathname === '/labels' && method === 'GET') {
    return json({ counts: mockLabelCounts, settings: labelSettingsStore });
  }
  if (pathname === '/labels/settings' && method === 'PUT') {
    labelSettingsStore = {
      ...labelSettingsStore,
      ...(body as Partial<LabelSettings>),
      updatedAt: Date.now(),
    };
    // Bare settings, like the server's PUT /api/labels/settings.
    return json(labelSettingsStore);
  }

  // --- automatiseringer (fase 5) ---
  if (pathname === '/automations' && method === 'GET') {
    const automations = Array.from(automationStore.values()).map((a) => ({
      ...a,
      stats: mockAutomationStats[a.id] ?? {
        enrolled: 0,
        active: 0,
        completed: 0,
        stopped: 0,
        sent: 0,
        revenue: 0,
        currency: 'EUR',
      },
    }));
    return json({ automations });
  }
  if (pathname === '/automations/builtins' && method === 'GET') {
    // Not part of docs/api-ui.md's documented surface, but handy for the
    // UI to know which of the 9 built-ins are still installable.
    return json({
      builtins: mockBuiltinFlows.map((b) => ({
        key: b.key,
        slug: b.slug,
        name: b.name,
        triggerType: b.triggerType,
      })),
    });
  }
  const previewMatch = /^\/automations\/builtins\/([^/]+)\/preview$/.exec(pathname);
  if (previewMatch && method === 'GET') {
    const builtin = mockBuiltinFlows.find((b) => b.key === previewMatch[1]);
    if (!builtin) return json({ error: 'not_found' }, 404);
    const mails = builtin.definition.steps
      .filter((s) => s.type === 'send_template')
      .map((s, i) => ({
        stepId: s.id,
        name: i === 0 ? null : `${builtin.name} ${i + 1}`,
        subject: String(s.subject),
        purpose: String(s.purpose),
        previewHtml:
          i === 0
            ? null
            : `<p style="font-family:sans-serif;padding:24px">Hi Anna, ${String(s.subject)}</p>`,
      }));
    return json({
      builtin: {
        key: builtin.key,
        slug: builtin.slug,
        name: builtin.name,
        triggerType: builtin.triggerType,
        triggerConfig: builtinTriggerConfig(builtin.key),
        allowedLegalBasis: builtin.allowedLegalBasis,
        allowReentry: builtin.allowReentry,
        stopOnPurchase: builtin.stopOnPurchase,
      },
      timeline: buildMockTimeline(builtin.definition),
      mails,
    });
  }
  const installMatch = /^\/automations\/builtins\/([^/]+)\/install$/.exec(pathname);
  if (installMatch && method === 'POST') {
    const key = installMatch[1] as string;
    const builtin = mockBuiltinFlows.find((b) => b.key === key);
    if (!builtin)
      return json({ error: 'invalid_request', message: `Unknown built-in flow '${key}'.` }, 400);
    if (Array.from(automationStore.values()).some((a) => a.slug === builtin.slug)) {
      return json(
        { error: 'already_exists', message: 'This automation is already installed.' },
        409,
      );
    }
    const id = `auto-${nextAutomationId++}`;
    const nowTs = Date.now();
    const automation: Automation = {
      id,
      slug: builtin.slug,
      name: builtin.name,
      builtInKey: builtin.key,
      triggerType: builtin.triggerType,
      triggerConfig: {},
      allowedLegalBasis: builtin.allowedLegalBasis,
      allowReentry: builtin.allowReentry,
      stopOnPurchase: builtin.stopOnPurchase,
      currentVersion: 1,
      enabled: false,
      enabledBy: null,
      createdAt: nowTs,
      updatedAt: nowTs,
    };
    automationStore.set(id, automation);
    automationVersionStore.set(id, [
      { version: 1, definition: builtin.definition, createdBy: 'u-mikkel', createdAt: nowTs },
    ]);
    return json({ automation }, 201);
  }

  const automationMatch = /^\/automations\/([^/]+)(\/.*)?$/.exec(pathname);
  if (automationMatch) {
    const id = automationMatch[1] as string;
    const sub = automationMatch[2] ?? '';
    const automation = automationStore.get(id);

    if (sub === '' && method === 'GET') {
      if (!automation) return json({ error: 'not_found' }, 404);
      const versions = automationVersionStore.get(id) ?? [];
      const current =
        versions.find((v) => v.version === automation.currentVersion) ??
        versions[versions.length - 1];
      const stats = mockAutomationStats[id] ?? {
        enrolled: 0,
        active: 0,
        completed: 0,
        stopped: 0,
        sent: 0,
        revenue: 0,
        currency: 'EUR',
      };
      return json({
        automation,
        definition: current?.definition ?? { steps: [] },
        versions,
        timeline: current ? buildMockTimeline(current.definition) : [],
        stats,
        stepStats: buildMockStepStats(current?.definition.steps ?? [], stats.enrolled),
      });
    }
    if (sub === '' && method === 'PUT') {
      if (!automation) return json({ error: 'not_found' }, 404);
      const definition = (body as { definition?: unknown })?.definition;
      if (
        !definition ||
        typeof definition !== 'object' ||
        !Array.isArray((definition as { steps?: unknown }).steps)
      ) {
        return json({ error: 'invalid_request', message: 'Body must contain "definition".' }, 400);
      }
      const nowTs = Date.now();
      const nextVersion = automation.currentVersion + 1;
      const versions = automationVersionStore.get(id) ?? [];
      versions.push({
        version: nextVersion,
        definition: definition as never,
        createdBy: 'u-mikkel',
        createdAt: nowTs,
      });
      automationVersionStore.set(id, versions);
      const updated: Automation = { ...automation, currentVersion: nextVersion, updatedAt: nowTs };
      automationStore.set(id, updated);
      return json({ automation: updated, definition });
    }
    if (sub === '/enable' && method === 'POST') {
      if (!automation) return json({ error: 'not_found' }, 404);
      const versions = automationVersionStore.get(id) ?? [];
      const current =
        versions.find((v) => v.version === automation.currentVersion) ??
        versions[versions.length - 1];
      const missingTemplateSteps = (current?.definition.steps ?? [])
        .filter((s) => s.type === 'send_template' && !s.templateId)
        .map((s) => s.id);
      if (missingTemplateSteps.length > 0) {
        return json(
          {
            error: 'missing_templates',
            message: `The flow cannot be activated — these steps are missing a template: ${missingTemplateSteps.join(', ')}.`,
            steps: missingTemplateSteps,
          },
          422,
        );
      }
      const updated: Automation = {
        ...automation,
        enabled: true,
        enabledBy: 'u-mikkel',
        updatedAt: Date.now(),
      };
      automationStore.set(id, updated);
      return json({ automation: updated });
    }
    if (sub === '/disable' && method === 'POST') {
      if (!automation) return json({ error: 'not_found' }, 404);
      const updated: Automation = { ...automation, enabled: false, updatedAt: Date.now() };
      automationStore.set(id, updated);
      return json({ automation: updated });
    }
  }

  // --- skabeloner (fase 5) ---
  if (pathname === '/contact-preview' && method === 'POST') {
    const b = body as { email?: string; subject?: string; html?: string };
    if (b?.email?.trim().toLowerCase() !== 'anna@example.com') {
      return json({ error: 'contact_not_found' }, 404);
    }
    return json({
      found: true,
      contact: {
        firstName: 'Anna',
        lastName: 'Example',
        language: 'de',
        country: 'DE',
        suppressed: false,
      },
      subject: (b.subject ?? '').replace(/\{\{\s*contact\.first_name[^}]*\}\}/g, 'Anna'),
      html: (b.html ?? '').replace(/\{\{\s*contact\.first_name[^}]*\}\}/g, 'Anna'),
      rules: [],
    });
  }
  if (pathname === '/templates' && method === 'GET') {
    const templates = Array.from(templateStore.values())
      .map((t) => ({
        id: t.id,
        name: t.name,
        kind: t.kind,
        updatedAt: t.updatedAt,
        usedBy: templateUsage(t.id),
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
    return json({ templates });
  }
  if (pathname === '/templates/html-check' && method === 'POST') {
    return json({
      report: mockHtmlCheck((body as { html?: string })?.html ?? '', mockSenderContext()),
    });
  }
  if (pathname === '/templates' && method === 'POST') {
    const b = body as Partial<Template>;
    const nowTs = Date.now();
    const kind = mockTemplateKind(b.blockJson);
    const html = kind === 'html' ? mockTranslateHtml(b.html ?? '<p></p>') : (b.html ?? '<p></p>');
    const template: Template = {
      id: `tpl-${nextTemplateId++}`,
      name: b.name ?? 'Ny skabelon',
      kind,
      blockJson: kind === 'blocks' ? b.blockJson : null,
      html,
      textFallback: html
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
      createdAt: nowTs,
      updatedAt: nowTs,
    };
    templateStore.set(template.id, template);
    return json({ template }, 201);
  }

  const templateMatch = /^\/templates\/([^/]+)(\/.*)?$/.exec(pathname);
  if (templateMatch) {
    const id = templateMatch[1] as string;
    const sub = templateMatch[2] ?? '';
    const template = templateStore.get(id);

    if (sub === '' && method === 'GET') {
      return template ? json({ template }) : json({ error: 'not_found' }, 404);
    }
    if (sub === '' && method === 'PUT') {
      if (!template) return json({ error: 'not_found' }, 404);
      const b = body as Partial<Template>;
      // A template keeps its kind: HTML never gets a block model, blocks never lose theirs.
      if (b.blockJson !== undefined && mockTemplateKind(b.blockJson) !== template.kind) {
        return json(
          {
            error: 'template_kind_locked',
            message:
              template.kind === 'html'
                ? 'This is an HTML template. It is edited as HTML and cannot be converted to blocks.'
                : 'This is a block template. It cannot be turned into an HTML template.',
          },
          409,
        );
      }
      const html =
        b.html === undefined
          ? template.html
          : template.kind === 'html'
            ? mockTranslateHtml(b.html)
            : b.html;
      const updated: Template = {
        ...template,
        ...b,
        kind: template.kind,
        blockJson: template.kind === 'html' ? null : (b.blockJson ?? template.blockJson),
        html,
        textFallback:
          b.html !== undefined
            ? html
                .replace(/<[^>]+>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
            : template.textFallback,
        // Like the API: a saved source that differs is the new original, so the section edits are dropped.
        sections: html === template.html ? (template.sections ?? null) : null,
        updatedAt: Date.now(),
      };
      templateStore.set(id, updated);
      return json({ template: updated });
    }
    if (sub === '/sections' && method === 'PUT') {
      if (mockRole === 'viewer') return json({ error: 'forbidden' }, 403);
      if (!template) return json({ error: 'not_found' }, 404);
      if (template.kind !== 'html') {
        return json(
          {
            error: 'sections_not_html',
            message: 'Sections are for HTML templates. This template is edited with blocks.',
          },
          409,
        );
      }
      const plan = mockSectionSave(body, mockSectionOriginal(template));
      if (!plan.ok) return json(plan.body, plan.status);
      const check = mockHtmlCheck(plan.html, mockSenderContext());
      if (!check.valid) return json({ error: 'template_invalid', errors: check.problems }, 400);
      const updated: Template = {
        ...template,
        html: plan.html,
        textFallback: mockPlainText(plan.html),
        sections: plan.sections,
        updatedAt: Date.now(),
      };
      templateStore.set(id, updated);
      return json({ template: updated, unmapped: plan.unmapped });
    }
    if (sub === '' && method === 'DELETE') {
      if (!template) return json({ error: 'not_found' }, 404);
      const inUseMessage = templateInUseMessage(id);
      if (inUseMessage) return json({ error: 'template_in_use', message: inUseMessage }, 409);
      templateStore.delete(id);
      // Sent campaigns keep their own html/subject snapshot, so nulling the FK on
      // the (mock) campaign store loses nothing — mirrors templates.repo.ts.
      for (const c of campaignStore.values()) {
        if (c.templateId === id) c.templateId = null;
      }
      return json({ status: 'success' });
    }
    if (sub === '/preview' && method === 'POST') {
      const html = (body as { html?: string })?.html ?? template?.html ?? '';
      const previewHtml = fillSampleMergeFields(html);
      return json({
        valid: true,
        errors: [],
        sanitizedHtml: html,
        previewHtml,
        sizeBytes: new TextEncoder().encode(html).length,
      });
    }
  }

  // --- mailchimp-import (P7, docs/decisions.md "Mailchimp-import 28/9") ---
  if (pathname === '/mailchimp-import/jobs' && method === 'GET') {
    const limit = Number(params.get('limit') ?? '20');
    const items = Array.from(importJobStore.values())
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, limit)
      .map(({ pendingRows: _pendingRows, ...job }) => job);
    return json({ items });
  }
  if (pathname === '/mailchimp-import/jobs' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { kind?: ImportJobKind; dryRun?: boolean; listId?: string };
    if (b.kind !== 'mailchimp_import' && b.kind !== 'mailchimp_unsubscribe_sync') {
      return json({ error: 'invalid_request' }, 400);
    }
    // Same list checks as apps/app/src/routes/mailchimpImport.ts.
    if (b.kind === 'mailchimp_import' && b.dryRun === false && b.listId === undefined) {
      return json(
        {
          error: 'list_required',
          message:
            'Choose the list the imported contacts are added to. Lists are created under Settings.',
        },
        400,
      );
    }
    if (b.listId !== undefined) {
      const list = listStore.get(b.listId);
      if (!list) return json({ error: 'unknown_list', message: 'The list does not exist.' }, 400);
      if (list.archived) {
        return json({ error: 'archived_list', message: 'The list is archived.' }, 400);
      }
    }
    const nowTs = Date.now();
    const job: MockImportJob = {
      id: `job-${nextImportJobId++}`,
      kind: b.kind,
      dryRun: b.dryRun ?? true,
      status: 'draft',
      listId: b.listId ?? null,
      createdAt: nowTs,
      updatedAt: nowTs,
      startedAt: null,
      completedAt: null,
      totalChunks: 0,
      processedChunks: 0,
      totalRows: 0,
      report: emptyMockReport(b.kind),
      error: null,
      pendingRows: [],
      columnPlans: [],
    };
    importJobStore.set(job.id, job);
    const { pendingRows: _pendingRows, ...jobJson } = job;
    return json(jobJson, 201);
  }
  const importJobMatch = /^\/mailchimp-import\/jobs\/([^/]+)(\/.*)?$/.exec(pathname);
  if (importJobMatch) {
    const job = importJobStore.get(importJobMatch[1] as string);
    const sub = importJobMatch[2] ?? '';
    if (sub === '' && method === 'GET') {
      if (!job) return json({ error: 'not_found' }, 404);
      const { pendingRows: _pendingRows, ...jobJson } = job;
      return json(jobJson);
    }
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    if (!job) return json({ error: 'not_found' }, 404);

    if (sub === '/files' && method === 'POST') {
      if (job.status !== 'draft') {
        return json({ error: 'invalid_job_state', message: 'The job is no longer a draft.' }, 409);
      }
      const b = (body ?? {}) as { fileName?: string; csvText?: string };
      const rows = csvStatusValues(b.csvText ?? '');
      job.pendingRows.push(rows);
      job.columnPlans.push(mockColumnPlan(b.csvText ?? ''));
      job.totalChunks += 1;
      job.totalRows += rows.length;
      job.updatedAt = Date.now();
      return json({ chunksAdded: 1, rowsAdded: rows.length }, 201);
    }

    if (sub === '/start' && method === 'POST') {
      if (job.status !== 'draft') {
        return json({ error: 'invalid_job_state', message: 'The job is not a draft.' }, 409);
      }
      if (job.totalChunks === 0) {
        return json(
          { error: 'invalid_job_state', message: 'Upload at least one file first.' },
          409,
        );
      }
      if (!job.dryRun) {
        const b = (body ?? {}) as { totpCode?: string };
        const stepUpError = mockWindowStepUp(b);
        if (stepUpError) return stepUpError;
      }
      job.status = 'running';
      job.startedAt = Date.now();
      job.updatedAt = job.startedAt;
      const { pendingRows: _pendingRows, ...jobJson } = job;
      return json(jobJson);
    }

    if (sub === '/run' && method === 'POST') {
      if (job.status === 'done') {
        const { pendingRows: _pendingRows, ...jobJson } = job;
        return json({ job: jobJson, done: true, chunksProcessedThisCall: 0 });
      }
      if (job.status !== 'running') {
        return json({ error: 'invalid_job_state', message: `The job is '${job.status}'.` }, 409);
      }
      const chunksProcessedThisCall = job.pendingRows.length;
      for (const rows of job.pendingRows) {
        for (const status of rows) {
          if (job.kind === 'mailchimp_import') {
            const report = job.report as Extract<ImportJob['report'], { contactsCreated: number }>;
            if (status === 'subscribed' || status === 'pending') {
              report.byStatus[status] += 1;
              report.contactsCreated += 1;
            } else if (status === 'unsubscribed') {
              report.byStatus.unsubscribed += 1;
              report.suppressionsAdded.unsubscribed += 1;
            } else if (status === 'cleaned') {
              report.byStatus.cleaned += 1;
              report.suppressionsAdded.hard_bounce += 1;
            } else if (status === 'nonsubscribed') {
              report.byStatus.nonsubscribed += 1;
            } else {
              report.byStatus.unknown += 1;
            }
          } else {
            const report = job.report as Extract<ImportJob['report'], { contactsDeleted: number }>;
            if (status === 'unsubscribed') {
              report.byStatus.unsubscribed += 1;
              report.suppressionsAdded.unsubscribed += 1;
              report.contactsDeleted += 1;
            } else if (status === 'cleaned') {
              report.byStatus.cleaned += 1;
              report.suppressionsAdded.hard_bounce += 1;
            } else {
              report.byStatus.other += 1;
            }
          }
        }
        job.report.filesProcessed += 1;
        job.report.totalRows += rows.length;
      }
      if (job.kind === 'mailchimp_import') {
        const report = job.report as Extract<ImportJob['report'], { contactsCreated: number }>;
        for (const plan of job.columnPlans) {
          for (const key of Object.keys(
            plan.recognised,
          ) as (keyof MockColumnPlan['recognised'])[]) {
            for (const name of plan.recognised[key]) {
              if (!report.attributes.columns.recognised[key].includes(name))
                report.attributes.columns.recognised[key].push(name);
            }
          }
          for (const name of plan.profile) {
            if (!report.attributes.columns.profile.includes(name))
              report.attributes.columns.profile.push(name);
          }
          for (const name of plan.ignored) {
            if (!report.attributes.columns.ignored.includes(name))
              report.attributes.columns.ignored.push(name);
            report.attributes.columns.ignoredValues[name] ??= 0;
          }
        }
        job.columnPlans = [];
      }
      job.pendingRows = [];
      job.processedChunks = job.totalChunks;
      job.status = 'done';
      job.completedAt = Date.now();
      job.updatedAt = job.completedAt;
      const { pendingRows: _pendingRows, ...jobJson } = job;
      return json({ job: jobJson, done: true, chunksProcessedThisCall });
    }

    if (sub === '/cancel' && method === 'POST') {
      if (job.status === 'done' || job.status === 'cancelled') {
        return json({ error: 'invalid_job_state', message: `The job is '${job.status}'.` }, 409);
      }
      // Unconditional step-up (unlike /start) — cancel always purges real
      // uploaded CSV content, dry run or not.
      const b = (body ?? {}) as { totpCode?: string };
      const stepUpError = mockWindowStepUp(b);
      if (stepUpError) return stepUpError;

      job.pendingRows = [];
      job.status = 'cancelled';
      job.completedAt = Date.now();
      job.updatedAt = job.completedAt;
      const { pendingRows: _pendingRows, ...jobJson } = job;
      return json(jobJson);
    }
  }

  // --- dmarc-cutover (P8, docs/decisions.md "DMARC-overgang 28/9") ---

  if (pathname === '/dmarc-cutover/reports' && method === 'GET') {
    const items = Array.from(dmarcReportStore.values())
      .sort((a, b) => b.uploadedAt - a.uploadedAt)
      .map(dmarcReportJson);
    return json({ items });
  }
  if (pathname === '/dmarc-cutover/reports' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as { fileName?: string; contentBase64?: string };
    if (!b.fileName || !b.contentBase64) return json({ error: 'invalid_request' }, 400);
    let xml: string;
    try {
      xml = atob(b.contentBase64);
    } catch {
      return json({ error: 'invalid_report', message: 'Could not decode the file.' }, 400);
    }
    if (!xml.includes('<feedback') || /<!doctype/i.test(xml) || /<!entity/i.test(xml)) {
      return json(
        {
          error: 'invalid_report',
          message: 'Only plain DMARC XML files are supported in mock mode.',
        },
        400,
      );
    }
    const parsed = parseMockDmarcXml(xml);
    if (!parsed)
      return json({ error: 'invalid_report', message: 'Could not read the report.' }, 400);
    const already = Array.from(dmarcReportStore.values()).some(
      (r) => r.orgName === parsed.report.orgName && r.reportId === parsed.report.reportId,
    );
    if (already) {
      return json(
        { error: 'already_exists', message: 'The report has already been uploaded.' },
        409,
      );
    }
    const row: MockDmarcReport = {
      id: `dmarc-report-${nextDmarcReportId++}`,
      sourceFileName: b.fileName,
      uploadedBy: 'mock-admin',
      uploadedAt: Date.now(),
      ...parsed.report,
    };
    dmarcReportStore.set(row.id, row);
    return json(dmarcReportJson(row), 201);
  }
  const dmarcReportMatch = /^\/dmarc-cutover\/reports\/([^/]+)$/.exec(pathname);
  if (dmarcReportMatch && method === 'GET') {
    const row = dmarcReportStore.get(dmarcReportMatch[1] as string);
    if (!row) return json({ error: 'not_found' }, 404);
    return json({ ...dmarcReportJson(row), records: row.records });
  }

  if (pathname === '/dmarc-cutover/known-senders' && method === 'GET') {
    return json({ items: Array.from(dmarcKnownSenderStore.values()) });
  }
  if (pathname === '/dmarc-cutover/known-senders' && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const b = (body ?? {}) as {
      label?: string;
      matchType?: KnownSenderMatchType;
      matchValue?: string;
    };
    if (!b.label || !b.matchType || !b.matchValue) return json({ error: 'invalid_request' }, 400);
    const row: KnownSender = {
      id: `dmarc-sender-${nextDmarcKnownSenderId++}`,
      label: b.label,
      matchType: b.matchType,
      matchValue: b.matchValue,
      createdAt: Date.now(),
    };
    dmarcKnownSenderStore.set(row.id, row);
    return json(row, 201);
  }
  const dmarcSenderMatch = /^\/dmarc-cutover\/known-senders\/([^/]+)$/.exec(pathname);
  if (dmarcSenderMatch && method === 'DELETE') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    dmarcKnownSenderStore.delete(dmarcSenderMatch[1] as string);
    return json({ ok: true });
  }

  if (pathname === '/dmarc-cutover/summary' && method === 'GET') {
    const days = Math.min(Math.max(Number(params.get('days') ?? '30') || 30, 1), 90);
    const since = Date.now() - days * 24 * 60 * 60 * 1000;
    const byDay = new Map<string, { total: number; pass: number }>();
    const bySender = new Map<string, number>();
    let totalCount = 0;
    let passCount = 0;
    for (const report of dmarcReportStore.values()) {
      if (report.dateEnd < since) continue;
      for (const rec of report.records) {
        const dayKey = new Date(report.dateEnd).toISOString().slice(0, 10);
        const bucket = byDay.get(dayKey) ?? { total: 0, pass: 0 };
        bucket.total += rec.count;
        const passed = rec.dkimResult === 'pass' || rec.spfResult === 'pass';
        if (passed) bucket.pass += rec.count;
        byDay.set(dayKey, bucket);
        totalCount += rec.count;
        if (passed) passCount += rec.count;
        const label = classifyMockSender(rec) ?? 'Unknown';
        bySender.set(label, (bySender.get(label) ?? 0) + rec.count);
      }
    }
    return json({
      days,
      totalCount,
      passRate: totalCount > 0 ? passCount / totalCount : null,
      byDay: Array.from(byDay.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([day, v]) => ({
          day,
          total: v.total,
          passRate: v.total > 0 ? v.pass / v.total : null,
        })),
      bySender: Array.from(bySender.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({ label, count, known: label !== 'Unknown' })),
    });
  }

  if (pathname === '/dmarc-cutover/checklist' && method === 'GET') {
    return json({ items: Array.from(dmarcChecklistStore.values()) });
  }
  const dmarcChecklistMatch = /^\/dmarc-cutover\/checklist\/([^/]+)$/.exec(pathname);
  if (dmarcChecklistMatch && method === 'POST') {
    if (mockRole !== 'admin') return json({ error: 'forbidden' }, 403);
    const key = dmarcChecklistMatch[1] as string;
    const existing = dmarcChecklistStore.get(key);
    if (!existing || existing.kind !== 'manual') return json({ error: 'not_found' }, 404);
    const b = (body ?? {}) as { checked?: boolean; note?: string };
    if (typeof b.checked !== 'boolean') return json({ error: 'invalid_request' }, 400);
    const now = Date.now();
    const updated: ChecklistItem = {
      ...existing,
      checked: b.checked,
      checkedBy: b.checked ? 'mock-admin' : null,
      checkedAt: b.checked ? now : null,
      note: b.note !== undefined ? b.note : existing.note,
      updatedAt: now,
    };
    dmarcChecklistStore.set(key, updated);
    return json(updated);
  }

  return json({ error: 'not_found', message: `No mock for ${method} ${pathname}` }, 404);
}

/** Bounces and complaints, newest first (Audience → Bounces); the oldest is from before SES's answer was kept. */
function mockBounces(): BounceDetail[] {
  const now = Date.now();
  const min = 60 * 1000;
  const halloween = { id: 'c1', name: 'Halloween 2026 (DE)' };
  const english = { id: 'c2', name: 'Halloween 2026 (EN)' };
  const welcome = { id: 'c3', name: 'Welcome flow (DA)' };
  const gmxFull =
    'smtp; 552-Requested mail action aborted: exceeded storage allocation 552-Quota exceeded.';
  const noBox = 'smtp; 550-Requested action not taken: mailbox unavailable';
  type Row = [
    string,
    BounceDetail['reason'],
    string | null,
    string | null,
    string | null,
    string | null,
    BounceDetail['campaign'],
  ];
  // email, reason, bounce type, sub type, diagnostic, suggested domain, mail
  const rows: Row[] = [
    [
      'lena.beispiel@t-online.example',
      'full',
      'Transient',
      'MailboxFull',
      gmxFull,
      null,
      halloween,
    ],
    ['tom.muster@yahoo.example', 'spam', 'abuse', null, null, null, halloween],
    [
      'k.muster@freenet.dd',
      'typo',
      'Permanent',
      'General',
      'smtp; 550 5.4.4 Invalid domain',
      'freenet.de',
      halloween,
    ],
    [
      'bart.muster@gmial.com',
      'typo',
      'Permanent',
      'General',
      'smtp; 511 sorry, no mailbox here by that name (#5.1.1)',
      'gmail.com',
      halloween,
    ],
    [
      'jan.beispiel@gmx.ed',
      'typo',
      'Permanent',
      'General',
      'smtp; 550 5.1.2 Host unknown',
      'gmx.de',
      halloween,
    ],
    ['info@salon-beispiel.example', 'nobox', 'Permanent', 'General', noBox, null, halloween],
    ['j.beispiel@gmx.example', 'nobox', 'Permanent', 'General', noBox, null, halloween],
    [
      'sara.test@mail.example',
      'full',
      'Transient',
      'MailboxFull',
      'smtp; 550 5.2.2 Mailbox Quota exceeded',
      null,
      halloween,
    ],
    ['mia.beispiel@web.example', 'full', 'Transient', 'MailboxFull', gmxFull, null, halloween],
    ['ugur.muster@web.example', 'full', 'Transient', 'MailboxFull', gmxFull, null, halloween],
    [
      'olivia.example@hotmial.com',
      'typo',
      'Permanent',
      'General',
      'smtp; 550 5.1.2 Host unknown',
      'hotmail.com',
      english,
    ],
    [
      'shop@old-company.example',
      'nodomain',
      'Permanent',
      'General',
      'smtp; 550 5.1.2 Host or domain name not found',
      null,
      english,
    ],
    ['m.beispiel@gmx.example', 'refused', 'Transient', 'General', null, null, halloween],
    [
      'jack.example@mail.example',
      'refused',
      'Transient',
      'General',
      'smtp; 421 4.7.0 Try again later',
      null,
      english,
    ],
    ['noah.example@outlook.example', 'nobox', 'Permanent', 'General', noBox, null, english],
    ['ella.example@icloud.example', 'spam', 'abuse', null, null, null, english],
    ['ida.eksempel@jubii.example', 'refused', 'Undetermined', 'Undetermined', null, null, english],
    [
      'pia.eksempel@blocked.example',
      'suppressed',
      'Permanent',
      'OnAccountSuppressionList',
      'smtp; 550 5.1.1 Address is on the suppression list',
      null,
      welcome,
    ],
    [
      'lars.eksempel@mail.example',
      'nobox',
      'Permanent',
      'General',
      'smtp; 550 5.1.1 User unknown',
      null,
      welcome,
    ],
    ['cy@legacy.example', 'nobox', null, null, null, null, null],
  ];
  return rows.map(
    ([email, reason, bounceType, subType, diagnostic, suggestedDomain, campaign], i) => ({
      id: `mock-bounce-${i + 1}`,
      email,
      occurredAt: now - (i * 37 + 12) * min,
      kind: reason === 'spam' ? 'complaint' : 'bounce',
      bounceType,
      subType,
      diagnostic,
      campaign,
      reason: mockCorrected(`mock-bounce-${i + 1}`, email, reason) ? 'corrected' : reason,
      suggestedDomain,
    }),
  );
}

/**
 * As `correctedTypoIds` in core: the gmx.ed typo's right address is already a contact,
 * and a fixed typo counts once its newsletter went again.
 */
function mockCorrected(id: string, email: string, reason: BounceReason): boolean {
  if (reason !== 'typo') return false;
  return email.endsWith('@gmx.ed') || (fixedBounceIds.has(id) && resentBounceIds.has(id));
}

/**
 * The open bounce row's contact facts. The gmial.com typo has a buyer behind the corrected
 * address (fixable), the gmx.ed one collides with an existing contact, the hotmial.com one
 * ordered with the typo itself, and the legacy address was never a contact.
 */
function mockBounceContact(b: BounceDetail): BounceContactOverview {
  const day = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const none = { count: 0, lastOrderedAt: null };
  const resend = mockResendOffer(b);
  const correctedEmail =
    (b.reason === 'typo' || b.reason === 'corrected') && b.suggestedDomain
      ? `${b.email.slice(0, b.email.lastIndexOf('@'))}@${b.suggestedDomain}`
      : null;
  const contactId = `mock-contact-${b.id}`;
  if (fixedBounceIds.has(b.id) && correctedEmail) {
    return {
      contact: null,
      orders: none,
      corrected: {
        email: correctedEmail,
        orders: { count: 1, lastOrderedAt: now - 60 * day },
        contactId,
      },
      resend,
    };
  }
  if (b.email === 'cy@legacy.example')
    return { contact: null, orders: none, corrected: null, resend: null };
  const fromShop = b.email.endsWith('@gmial.com') || b.email.endsWith('@hotmial.com');
  return {
    contact: {
      id: contactId,
      status: 'active',
      source: fromShop ? 'Mailchimp for WooCommerce' : 'List Import from File Upload',
      consentCategory: fromShop ? 'single_optin_with_time_and_ip' : 'time_only',
      language: b.email.endsWith('@hotmial.com') ? 'en' : 'de',
      country: b.email.endsWith('@hotmial.com') ? 'GB' : 'DE',
      createdAt: now - 6 * day,
      signedUpAt: now - (fromShop ? 420 : 2900) * day,
      orderSignupAt: null,
    },
    orders: b.email.endsWith('@hotmial.com') ? { count: 1, lastOrderedAt: now - 550 * day } : none,
    corrected: correctedEmail
      ? {
          email: correctedEmail,
          orders: b.email.endsWith('@gmial.com')
            ? { count: 1, lastOrderedAt: now - 60 * day }
            : none,
          contactId: b.email.endsWith('@gmx.ed') ? 'mock-contact-existing' : null,
        }
      : null,
    resend,
  };
}

/** Newsletters (not flow mails) went out yesterday afternoon and can go once more. */
function mockResendOffer(b: BounceDetail): BounceContactOverview['resend'] {
  if (!b.campaign || b.campaign.id === 'c3') return null;
  return {
    campaign: { ...b.campaign, sentAt: Date.now() - 26 * 60 * 60 * 1000 },
    messageId: `mock-message-${b.id}`,
    blocked: resentBounceIds.has(b.id) ? 'already_resent' : null,
  };
}

function mockResend(b: BounceDetail) {
  const offer = mockResendOffer(b);
  if (!offer) return { status: 'not_sent', reason: 'not_offered' };
  if (offer.blocked) return { status: 'not_sent', reason: offer.blocked };
  resentBounceIds.add(b.id);
  return { status: 'sent' };
}

/** Unsubscribes, newest first (Audience → Unsubscribed); the older one has no address. */
function mockUnsubscribes() {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  return [
    {
      id: 'mock-unsub-2',
      email: 'anna@gmx.example',
      occurredAt: now - 2 * 60 * 60 * 1000,
      source: 'link_click',
      reason: 'too_many',
      reasonAt: now - 2 * 60 * 60 * 1000 + 30_000,
      campaign: { id: 'c1', name: 'Halloween' },
      language: 'da',
      country: 'DK',
      signupSource: 'Mailchimp for WooCommerce',
      subscribedOn: now - 400 * day,
    },
    {
      id: 'mock-unsub-1',
      email: null,
      occurredAt: now - 3 * day,
      source: 'link_click',
      reason: null,
      reasonAt: null,
      campaign: null,
      language: null,
      country: null,
      signupSource: null,
      subscribedOn: null,
    },
  ] satisfies UnsubscribeDetail[];
}
